import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { DealDocumentRequirement, DealMedication, DealMedicationSummary, TransportJourneySummary } from '../data/api';
import { fetchTransportJourneys, submitTreatmentInvoice, createSharedFinance, fetchSharedFinances, fetchDealDocumentsForDeal, fetchDealFeedRecords, fetchDealFeedAllocation, deleteDealFeedRecord, uploadDealDocument, fetchDealDocumentTypeOptions } from '../data/api';
import type { DealDocumentSummary, DealFeedRecord, DealFeedAllocation } from '../data/api';
import CostLineItems, { costLineIsComplete, costLinesTotal, newCostLine, splitByHead } from './CostAllocationPicker';
import type { CostLine } from './CostAllocationPicker';
import { DealHealthPanel } from './DealHealthPanel';
import { DealDocumentChecklist } from './DealDocumentChecklist';
import {
  DealTimeline,
  DealAdminReport,
  DealIntegrations,
  DealEventAnimal,
  RawDataRowProvenance,
  TrackingData,
  fetchTracking,
  fetchDealTimeline,
  fetchDealAdminReport,
  fetchDealIntegrations,
  fetchDealVaccinations,
  fetchAnimals,
  fetchFeedlotFee,
  fetchSessionRawRows,
  eventCurrentSessionId,
  fetchDealEventAnimals,
  linkAnimalsToDeal,
  LinkAnimalConflict,
  updateDeal,
  fetchDealFinances,
  createDealFinance,
  deleteDealFinance,
  DEAL_FINANCE_TYPES,
  type DealFinanceRow,
  type CreateDealFinanceInput,
  fetchDealDocumentRequirements,
  mapCompletionOverride,
  requestDealCompletionOverride,

  fetchDealById,
  mapDealMedication,
  mapDealMedicationSummary,} from '../data/api';
import ValueChainBanner from './ValueChainBanner';
import DealEventsSection from './DealEventsSection';
import DealCostModel from './DealCostModel';
import DealCompletionOverridePanel from './DealCompletionOverridePanel';
import SharedFeedlotCosts from './SharedFeedlotCosts';
import RetagCandidates, { RetagDialog } from './RetagPanel';
import OwnershipFlow from './OwnershipFlow';
import { useConfirm } from './ConfirmDialog';
import FileDropzone from './FileDropzone';
import { auditCounts } from './auditCounts';
import { RawDataModal, RawDataModalState, formatRevisionTimestamp } from './RawDataViewer';
import { diffRawRevisions, summariseRevisionDiff } from './rawRevisionDiff';
import { MEDIA_ORIGIN } from '../auth/config';

// Minimal structural shape the overview needs from a deal row. Kept local (not
// tied to api's DealSummary) so the parent screen's own Deal type is assignable.
export interface OverviewDeal {
  dealId: string;
  title: string;
  counterpart: string;
  startDate?: string;
  headcount: number;
  status: string;
  species?: string | null;
  dealRefNo?: string;
  deal_reference_no?: string;
}

// =============================================================================
// Formatting helpers
// =============================================================================

const nf = new Intl.NumberFormat();
const fmtNum = (value: unknown) => nf.format(Math.round(Number(value) || 0));

const zar = new Intl.NumberFormat('en-ZA', { style: 'currency', currency: 'ZAR', maximumFractionDigits: 2 });
const fmtMoney = (value: unknown): string => {
  if (value === null || value === undefined || value === '' || !Number.isFinite(Number(value))) return '—';
  return zar.format(Number(value));
};

const fmtDate = (value?: string | null): string => {
  if (!value) return '—';
  const iso = String(value).split('T')[0];
  return iso || '—';
};

const SPECIES_LABELS: Record<string, string> = { SHEEP: 'Sheep', CATTLE: 'Cattle', GOAT: 'Goats' };
const speciesLabel = (code?: string | null): string => {
  if (!code) return '';
  return SPECIES_LABELS[String(code).toUpperCase()] || String(code);
};

// Media URLs from the API come back either absolute (GCS) or relative; normalise
// both to the backend media host for the active environment (see auth/config).
const normalizeDocumentUrl = (url?: string): string => {
  if (!url) return '';
  if (url.startsWith('https://storage.googleapis.com/')) {
    return url.replace(/https:\/\/storage\.googleapis\.com\/[^/]+\/media\//, `${MEDIA_ORIGIN}/media/`);
  }
  if (!url.startsWith('http')) return `${MEDIA_ORIGIN}${url}`;
  return url;
};

const EVENT_TYPE_LABELS: Record<string, string> = {
  FARM_DIS: 'Farm dispatch',
  FARM_REC: 'Farm receive',
  FEED_DIS: 'Feedlot dispatch',
  FEED_REC: 'Feedlot receive',
  ABAT_REC: 'Abattoir receive',
  FARM_BIR: 'Farm birth',
  FARM_REG: 'Farm registration',
  FEED_REG: 'Feedlot registration',
  ABAT_REG: 'Abattoir registration',
  FARM_SHE: 'Farm shear',
  FEED_SHE: 'Feedlot shear',
  FARM_MOR: 'Farm mortality',
  FEED_MOR: 'Feedlot mortality',
  ABAT_MOR: 'Abattoir mortality',
  FARM_WEI: 'Farm weigh',
  FEED_WEI: 'Feedlot weigh',
  ABAT_SLA: 'Abattoir slaughter',
  FEED_WOO: 'Wool batch',
  FARM_BAL: 'Farm bale',
  FEED_BAL: 'Feedlot bale',
  ABAT_DIS: 'Abattoir dispatch',
  WOOL_ANA: 'Wool analysis',
  VACCINATION: 'Vaccination',
  FEED_VAC: 'Feedlot vaccination',
};

const eventLabel = (code?: string | null) => (code ? EVENT_TYPE_LABELS[code] || code : '—');

// Sync-status pill styling shared by RMIS + Virtual Routez badges.
const STATUS_PILL: Record<string, { label: string; cls: string }> = {
  SENT: { label: 'Synced', cls: 'bg-[#e7f8ee] text-[#106b38] border-[#b9e7c9]' },
  SUCCESS: { label: 'Sent', cls: 'bg-[#e7f8ee] text-[#106b38] border-[#b9e7c9]' },
  FAILED: { label: 'Failed', cls: 'bg-[#f8eae7] text-[#6b1010] border-[#e7bbb9]' },
  TIMEOUT: { label: 'Timed out', cls: 'bg-[#f8eae7] text-[#6b1010] border-[#e7bbb9]' },
  SKIPPED: { label: 'Skipped', cls: 'bg-[#fff7ef] text-[#7b4a18] border-[#ecd1b9]' },
  PENDING: { label: 'Pending', cls: 'bg-[#e8f4ff] text-[#0f3b64] border-[#1e6bb833]' },
};

const StatusPill = ({ status }: { status?: string | null }) => {
  if (!status) return <span className="text-xs text-avcs-muted">—</span>;
  const badge = STATUS_PILL[status];
  return (
    <span className={`inline-flex rounded-full border px-2 py-0.5 text-xs font-semibold ${badge?.cls || 'bg-[#eef2f5] text-avcs-muted border-[#d7dee3]'}`}>
      {badge?.label || status}
    </span>
  );
};

// =============================================================================
// Section shell
// =============================================================================

const Section = ({
  title,
  subtitle,
  actions,
  children,
}: {
  title: string;
  subtitle?: string;
  actions?: React.ReactNode;
  children: React.ReactNode;
}) => (
  <div className="min-w-0 rounded-xl border border-[#1e6bb824] bg-[#fbfeff] p-4">
    <div className="flex flex-wrap items-start justify-between gap-2">
      <div>
        <h3 className="text-lg font-semibold text-avcs-text">{title}</h3>
        {subtitle && <p className="mt-0.5 text-xs text-avcs-muted">{subtitle}</p>}
      </div>
      {actions}
    </div>
    <div className="mt-3 min-w-0">{children}</div>
  </div>
);

// =============================================================================
// Cost stack grouping (value-chain phases)
// =============================================================================

const COST_GROUPS: { label: string; keys: string[] }[] = [
  { label: 'Movement / transport', keys: ['transport_cost'] },
  { label: 'Feedlot', keys: ['feed_consumption_cost', 'feedlot_finance_cost', 'feedlot_daily_cost_estimate'] },
  { label: 'Health / treatment', keys: ['vaccination_cost', 'deal_vaccination_cost'] },
  { label: 'Registration & tags', keys: ['animal_purchase_cost', 'animal_data_processing_cost', 'eid_tag_cost'] },
  { label: 'Wool / shearing', keys: ['shearing_cost'] },
  { label: 'Equipment', keys: ['equipment_cost'] },
  { label: 'Other', keys: ['deal_finance_expenses'] },
];

const COST_KEY_LABELS: Record<string, string> = {
  transport_cost: 'Transport journeys',
  feed_consumption_cost: 'Feed consumption',
  feedlot_finance_cost: 'Feedlot invoices',
  feedlot_daily_cost_estimate: 'Feedlot daily-cost estimate',
  vaccination_cost: 'Per-animal vaccinations',
  deal_vaccination_cost: 'Deal vaccinations',
  animal_purchase_cost: 'Animal purchase',
  animal_data_processing_cost: 'Data processing',
  eid_tag_cost: 'EID tags',
  shearing_cost: 'Shearing',
  equipment_cost: 'Equipment',
  deal_finance_expenses: 'Booked on the ledger (medicine, feed, other costs)',
};

// =============================================================================
// Main component
// =============================================================================

type DealTab = 'dashboard' | 'events' | 'review' | 'details' | 'financial' | 'health' | 'documents' | 'transport' | 'integrations' | 'audit' | 'feed' | 'animals' | 'slaughter' | 'costModel';

type DetailsSubTab = 'ownership' | 'transaction' | 'seller' | 'purchaser' | 'animals' | 'origin';

const DETAILS_SUB_TABS: { key: DetailsSubTab; label: string }[] = [
  { key: 'transaction', label: 'Transaction' },
  { key: 'ownership', label: 'Ownership flow' },
  { key: 'seller', label: 'Seller' },
  { key: 'purchaser', label: 'Purchaser' },
  { key: 'animals', label: 'Animals & pricing' },
  { key: 'origin', label: 'Origin' },
];

const DEAL_TABS: { key: DealTab; label: string }[] = [
  { key: 'dashboard', label: 'Dashboard' },
  { key: 'events', label: 'Events' },
  { key: 'details', label: 'Deal details' },
  { key: 'financial', label: 'Financial' },
  { key: 'costModel', label: 'Margin Analysis' },
  { key: 'health', label: 'Health & vax' },
  { key: 'documents', label: 'Documents' },
  { key: 'transport', label: 'Transport' },
  { key: 'integrations', label: 'Integrations' },
  { key: 'audit', label: 'Audit trail' },
  { key: 'feed', label: 'Feed data' },
  { key: 'animals', label: 'Animals' },
  { key: 'slaughter', label: 'Slaughter data' },
  // Last on purpose: the deal's closing statement, read once the animals are through.
];

// Display labels for the capture enums the details tab renders. Falling back
// to a prettified code keeps an unmapped value readable rather than raw.
const CAPTURE_CODE_LABELS: Record<string, string> = {
  BRANDING: 'Branding',
  TATTOO: 'Tattoo',
  EAR_TAGS: 'Ear tags',
  RFID_EAR_TAGS: 'RFID ear tags (EID)',
  NOTCHING: 'Notching',
  IMPLANTED_MICROCHIP: 'Implanted microchip',
  PURCHASED: 'Purchased',
  BORN_ON_FARM: 'Born on farm',
  AUCTION: 'Auction',
  PRIVATE_SALE: 'Private sale',
  INDIVIDUAL: 'Individual',
  ENTITY: 'Entity',
};

const captureLabel = (code?: string | null): string => {
  if (!code) return '';
  if (CAPTURE_CODE_LABELS[code]) return CAPTURE_CODE_LABELS[code];
  const pretty = String(code).replace(/_/g, ' ').toLowerCase();
  return pretty.charAt(0).toUpperCase() + pretty.slice(1);
};

const yesNo = (value: any): string => (value === true ? 'Yes' : value === false ? 'No' : 'Not answered');

// One key–value row grid, shared by the details tab's sections. A missing
// value renders as an em dash so "not captured" stays visible rather than
// the row disappearing.
const DetailRows: React.FC<{ rows: Array<[string, React.ReactNode]> }> = ({ rows }) => (
  <div className="grid gap-2 sm:grid-cols-2">
    {rows.map(([label, value]) => (
      <div key={label} className="flex items-center justify-between gap-3 rounded-lg border border-[#1e6bb824] bg-white px-3 py-2 text-sm">
        <span className="text-avcs-muted">{label}</span>
        <span className="text-right font-medium text-avcs-text">{value === '' || value == null ? '—' : value}</span>
      </div>
    ))}
  </div>
);

// The seller/purchaser block the amendments spec asks for, read off the
// nested party record on the deal detail.
const partyRows = (party: any, opts: { vat?: boolean } = {}): Array<[string, React.ReactNode]> => {
  if (!party) return [];
  const withVat = opts.vat !== false;
  const address = [party.address_line1, party.address_line2, party.city, party.state, party.postal_code]
    .filter(Boolean).join(', ');
  const rows: Array<[string, React.ReactNode]> = [
    ['Name', party.entity_name || '—'],
    ['Type', party.entity_type === 'OTHER' && party.entity_type_other
      ? `Other — ${party.entity_type_other}`
      : captureLabel(party.entity_type)],
    ['Individual / entity', party.party_kind ? captureLabel(party.party_kind) : 'Not captured'],
    [party.party_kind === 'INDIVIDUAL' ? 'ID number' : 'Registration number', party.identity_number || '—'],
  ];
  if (party.registered_business_name) rows.push(['Registered business name', party.registered_business_name]);
  rows.push(
    ['Address', address || '—'],
    ['Farm name', party.farm_name || '—'],
    ['District', party.district || '—'],
    ['Province', party.province || '—'],
  );
  if (withVat) {
    rows.push(['VAT registered', yesNo(party.vat_registered)]);
    if (party.vat_registered === true) rows.push(['VAT number', party.vat_number || '—']);
  }
  rows.push(['GLN applicable to transaction', yesNo(party.gln_applicable)]);
  return rows;
};

export interface DealOverviewProps {
  deals: OverviewDeal[];
  animalSummaryByDeal?: Record<string, { total?: number; species?: Record<string, number>; breeds?: Record<string, number> }>;
  selectedDealId: string;
  onSelectDeal: (id: string) => void;
  onBack: () => void;
  onEditFeed: (id: string) => void;
  /** Opens Event Management → Add feed with this deal preselected. */
  onAddFeed?: (id: string) => void;
  onUploadDocs: (id: string) => void;
  onViewAnimals: (id: string) => void;
  onViewAnimal?: (animalId: string, fromTab?: string) => void;
  /** Tab to open on mount, so Back from an animal lands where the user left. */
  initialTab?: string;
  onNewEvent?: (dealId: string) => void;
  onDealStatusChanged?: (dealId: string, isCompleted: boolean) => void;
  /** Opens the event-capture modal preset to a vaccination file for this deal. */
  onNewVaccination?: (dealId: string) => void;
  /** Opens the journey-capture modal preset to this deal. */
  onNewJourney?: (dealId: string) => void;
  /** Resumes the new-deal wizard on this deal (step 2 onwards). */
  onOpenWizard?: (dealId: string) => void;
  /** Bumped by the parent when one of those modals closes, so the tabs re-read. */
  refreshToken?: number;
}

/** Raw slaughter values longer than this are truncated with expand-on-click. */
const SLAUGHTER_CELL_MAX = 48;

/**
 * Raw columns that identify the animal. A blank in one of these is worth
 * explaining; a blank in `grade` or `weight` is just a blank.
 */
const IDENTIFIER_COLUMNS = new Set(['eid', 'ear_tag', 'eartag', 'vid', 'tag', 'tag_number']);

export default function DealOverview({
  deals,
  animalSummaryByDeal = {},
  selectedDealId,
  onBack,
  onEditFeed,
  onAddFeed,
  onUploadDocs,
  onViewAnimals,
  onViewAnimal,
  initialTab,
  onNewEvent,
  onDealStatusChanged,
  onNewVaccination,
  onNewJourney,
  onOpenWizard,
  refreshToken = 0,
}: DealOverviewProps) {
  const confirm = useConfirm();
  const [tracking, setTracking] = useState<TrackingData | null>(null);
  const [timeline, setTimeline] = useState<DealTimeline | null>(null);
  const [report, setReport] = useState<DealAdminReport | null>(null);
  const [integrations, setIntegrations] = useState<DealIntegrations | null>(null);
  const [vaccinations, setVaccinations] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [auditFilter, setAuditFilter] = useState<'all' | 'integrations'>('all');
  // Audit trail — "Raw data" drill-in: the raw rows of the file an event was built from.
  const [rawData, setRawData] = useState<RawDataModalState | null>(null);
  const [tab, setTab] = useState<DealTab>('dashboard');
  // Consumed once: the mount-time deal effect below applies it, later deal changes reset to Dashboard.
  const initialTabRef = useRef<string | undefined>(initialTab);
  // Documents tab: the filed document whose cost lines are being captured.
  const [costLinesDocId, setCostLinesDocId] = useState('');
  // The Documents tab's three parts, and its two small upload forms.
  const [docSubTab, setDocSubTab] = useState<'required' | 'other' | 'revenue'>('required');
  const [docTypeOptions, setDocTypeOptions] = useState<Array<{ id: string; label: string; code?: string; financeRecordType?: string }>>([]);
  const [otherDocForm, setOtherDocForm] = useState<{ description: string; cost: string; file: File | null }>({ description: '', cost: '', file: null });
  const [revenueForm, setRevenueForm] = useState<{ value: string; date: string; file: File | null }>({ value: '', date: '', file: null });
  const [docBusy, setDocBusy] = useState('');
  const [docMessage, setDocMessage] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null);
  useEffect(() => {
    if (tab !== 'documents' || docTypeOptions.length > 0) return;
    fetchDealDocumentTypeOptions().then((options) => setDocTypeOptions(options as any)).catch(() => {});
  }, [tab, docTypeOptions.length]);
  // Feed data tab: the add-feed form replaces the tiles while open.
  const [feedRecords, setFeedRecords] = useState<DealFeedRecord[]>([]);
  const loadFeedRecords = useCallback(async () => {
    if (!selectedDealId) { setFeedRecords([]); return; }
    try { setFeedRecords(await fetchDealFeedRecords(selectedDealId)); } catch { setFeedRecords([]); }
  }, [selectedDealId]);
  useEffect(() => { loadFeedRecords(); }, [loadFeedRecords]);

  // Deal details tab: read-only by default; "Edit" swaps in a form over the
  // same fields and saves through the existing PATCH. Party (seller/purchaser)
  // identity is an entity, edited on the entity screen, so it is not here.
  const [detailsEditing, setDetailsEditing] = useState(false);
  const [detailsSaving, setDetailsSaving] = useState(false);
  const [detailsError, setDetailsError] = useState('');
  const [detailsForm, setDetailsForm] = useState<Record<string, string>>({});
  const beginDetailsEdit = (detail: any) => {
    const s = (v: any) => (v == null ? '' : String(v));
    setDetailsForm({
      dealReferenceNo: s(detail?.deal_reference_no),
      dealDate: s(detail?.deal_date).slice(0, 10),
      totalAnimals: s(detail?.total_animals),
      totalMale: s(detail?.total_male),
      totalFemale: s(detail?.total_female),
      identificationType: s(detail?.identification_type),
      totalWeightKg: s(detail?.total_weight_kg),
      priceMale: s(detail?.price_male),
      priceFemale: s(detail?.price_female),
      totalPriceExVat: s(detail?.total_price_ex_vat),
      totalPriceInclVat: s(detail?.total_price_incl_vat),
      penaltyTerms: s(detail?.penalty_terms),
      origin: s(detail?.origin),
      purchaseChannel: s(detail?.purchase_channel),
      traceabilityDocumented: detail?.traceability_documented === true ? 'yes' : 'no',
      sellerAgentName: s(detail?.seller_agent_name),
      sellerAgentContact: s(detail?.seller_agent_contact),
      purchaserAgentName: s(detail?.purchaser_agent_name),
      purchaserAgentContact: s(detail?.purchaser_agent_contact),
    });
    setDetailsError('');
    setDetailsEditing(true);
  };

  // Animals tab: deal-scoped standard + slaughter lists, loaded on demand.

  const [dealAnimals, setDealAnimals] = useState<any[]>([]);
  const [dealAnimalsLoading, setDealAnimalsLoading] = useState(false);
  const [dealAnimalsError, setDealAnimalsError] = useState('');
  const [dealAnimalsSearch, setDealAnimalsSearch] = useState('');
  // Re-tagging: the row the operator picked, and a bump to reload the list
  // after a link, since a merge can remove a row.
  // Details sub-tabs. The details tab is five dense blocks of reference data,
  // and an operator opens it to check one party, not to read all five.
  const [detailsSubTab, setDetailsSubTab] = useState<DetailsSubTab>('transaction');
  // The feedlot's occupancy charge, booked and still accruing.
  const [feedlotFee, setFeedlotFee] = useState<Awaited<ReturnType<typeof fetchFeedlotFee>> | null>(null);
  const [retagTarget, setRetagTarget] = useState<{ id: string; eid: string } | null>(null);
  const [retagRefresh, setRetagRefresh] = useState(0);
  const [dealAnimalsPage, setDealAnimalsPage] = useState(1);
  // Slaughter tab — the raw ABAT_SLA upload rows themselves, not the deal's
  // animals. Reading the file (rather than the deal-scoped per-animal endpoint)
  // is what makes rows whose animal never linked to the deal visible at all;
  // through the animal endpoint they are invisible by construction.
  const [slaughterRawRows, setSlaughterRawRows] = useState<RawDataRowProvenance[]>([]);
  const [slaughterLoading, setSlaughterLoading] = useState(false);
  const [slaughterError, setSlaughterError] = useState('');
  const [slaughterLoaded, setSlaughterLoaded] = useState(false);
  const [slaughterSearch, setSlaughterSearch] = useState('');
  // Long free-text raw values (abattoir `notes` runs to a paragraph) are
  // truncated to one line and expanded on click, keyed `rowId:column`. Keyed
  // by length rather than by column name so any long column behaves the same
  // -- the field is called `notes` on Cavalier's file and need not be on the
  // next abattoir's.
  const [slaughterExpanded, setSlaughterExpanded] = useState<Set<string>>(new Set());
  // Revision history for this deal's slaughter file(s). Shown only when a file
  // has actually been revised -- an unrevised deal gets no strip rather than a
  // permanent "revision 1 of 1".
  const [slaughterRevisionsOpen, setSlaughterRevisionsOpen] = useState(false);
  // `revision_root_id -> summary of what the current revision changed`, filled
  // lazily when the history is opened. The backend computes an equivalent
  // breakdown at upload time but does not persist it, so it is recomputed from
  // the rows (both revisions' rows survive a re-upload). See rawRevisionDiff.
  const [slaughterDiffs, setSlaughterDiffs] = useState<Record<string, string>>({});
  const [slaughterDiffsLoading, setSlaughterDiffsLoading] = useState(false);

  // Deal open/closed status: optimistic override + confirm/save state so the
  // badge reflects the change instantly, then bubbles up via onDealStatusChanged.
  const [completedOverride, setCompletedOverride] = useState<boolean | null>(null);
  const [cancelledOverride, setCancelledOverride] = useState<boolean | null>(null);
  const [statusSaving, setStatusSaving] = useState(false);
  const [statusError, setStatusError] = useState('');
  const [confirmingStatus, setConfirmingStatus] = useState(false);

  const selectedDeal = useMemo(() => deals.find((d) => d.dealId === selectedDealId) || null, [deals, selectedDealId]);
  const isCompleted = completedOverride ?? (selectedDeal?.status === 'Completed');
  const isCancelled = cancelledOverride ?? (selectedDeal?.status === 'Cancelled');
  // Cancelled outranks completed: a deal that was called off did not run to
  // completion, so it must not read "Completed" in the header.
  const statusLabel = isCancelled ? 'Cancelled' : isCompleted ? 'Completed' : 'Active';

  // Breed breakdown for the selected deal, from the per-deal animal-summary
  // aggregate (no per-animal fetch). Sorted most-common first.
  // ---- The deal's money ledger (DealFinance rows) ------------------------
  const [finances, setFinances] = useState<DealFinanceRow[]>([]);
  const loadFinances = useCallback(async () => {
    if (!selectedDealId) { setFinances([]); return; }
    try {
      setFinances(await fetchDealFinances(selectedDealId));
    } catch {
      setFinances([]);  // non-admin or endpoint absent -- the ledger just reads empty
    }
  }, [selectedDealId]);
  useEffect(() => { loadFinances(); }, [loadFinances]);

  // A capture modal (vaccination file, journey) closed on this deal: re-read
  // what it could have changed, without the full reload that resets the tab.
  useEffect(() => {
    if (!selectedDealId || !refreshToken) return;
    fetchTracking(selectedDealId).then(setTracking).catch(() => {});
    fetchDealVaccinations(selectedDealId).then((rows) => setVaccinations(Array.isArray(rows) ? rows : [])).catch(() => {});
    fetchTransportJourneys(selectedDealId).then(setDealJourneys).catch(() => {});
    loadFinances();
  }, [refreshToken]);

  // ---- Required documents for this deal ----------------------------------
  const [docRequirements, setDocRequirements] = useState<DealDocumentRequirement[]>([]);
  const [canCompleteDeal, setCanCompleteDeal] = useState(true);

  // The full detail record, fetched once. It carries the document checklist,
  // the health declaration, the medications and the feeding declaration — so
  // one request serves three panels rather than three requests serving one each.
  const [dealDetail, setDealDetail] = useState<any | null>(null);
  const [dealMedications, setDealMedications] = useState<DealMedication[]>([]);
  const [medicationSummary, setMedicationSummary] = useState<DealMedicationSummary | null>(null);

  const loadDocRequirements = useCallback(async () => {
    if (!selectedDealId) {
      setDocRequirements([]);
      setCanCompleteDeal(true);
      setDealDetail(null);
      setDealMedications([]);
      setMedicationSummary(null);
      return;
    }
    try {
      const detail = await fetchDealById(selectedDealId);
      setDealDetail(detail);
      setDocRequirements(
        Array.isArray(detail?.document_requirements) ? detail.document_requirements : [],
      );
      setCanCompleteDeal(detail?.can_complete === true);
      setDealMedications(
        Array.isArray(detail?.medications) ? detail.medications.map(mapDealMedication) : [],
      );
      setMedicationSummary(
        detail?.medication_summary ? mapDealMedicationSummary(detail.medication_summary) : null,
      );
    } catch {
      // Non-fatal: the completion gate is enforced server-side regardless of
      // whether these panels rendered, so a failure must not block the screen.
      setDealDetail(null);
      setDocRequirements([]);
      setCanCompleteDeal(true);
      setDealMedications([]);
      setMedicationSummary(null);
    }
  }, [selectedDealId]);

  useEffect(() => { loadDocRequirements(); }, [loadDocRequirements]);

  // Close-out override state, read off the same detail record: the override
  // the deal closed under, else the latest request. Kept as one object so the
  // panel, the checklist banner and the header pill cannot disagree.
  const completionOverride = useMemo(
    () => (dealDetail?.completion_override ? mapCompletionOverride(dealDetail.completion_override) : null),
    [dealDetail],
  );
  const completedUnderOverride = dealDetail?.completed_under_override === true;
  const requestOverride = async (reason: string) => {
    if (!selectedDealId) return;
    await requestDealCompletionOverride(selectedDealId, reason);
    await loadDocRequirements();
  };

  // ---- Transport journeys for this deal -----------------------------------
  // The amendments spec's Transportation block is captured per journey; the
  // deal screen shows each journey with those fields under its own tab.
  const [dealJourneys, setDealJourneys] = useState<TransportJourneySummary[]>([]);
  useEffect(() => {
    let cancelled = false;
    if (!selectedDealId) { setDealJourneys([]); return; }
    fetchTransportJourneys(selectedDealId)
      .then((rows) => { if (!cancelled) setDealJourneys(rows); })
      .catch(() => { if (!cancelled) setDealJourneys([]); });
    return () => { cancelled = true; };
  }, [selectedDealId]);

  // ---- Linking an animal into this deal on demand ------------------------
  const [linkBusy, setLinkBusy] = useState('');
  const [pendingForceIds, setPendingForceIds] = useState<string[]>([]);
  const [linkError, setLinkError] = useState('');
  const [linkNotice, setLinkNotice] = useState('');

  const linkAnimals = async (animalIds: string[], force = false) => {
    if (!selectedDealId || animalIds.length === 0) return;
    setLinkBusy(animalIds.length === 1 ? animalIds[0] : 'bulk');
    setLinkError('');
    setLinkNotice('');
    try {
      const result = await linkAnimalsToDeal(selectedDealId, animalIds, force);
      const parts: string[] = [];
      if (result.linked.length) parts.push(`${result.linked.length} linked`);
      if (result.moved_from_other_deal.length) parts.push(`${result.moved_from_other_deal.length} moved from another deal`);
      if (result.already_on_deal.length) parts.push(`${result.already_on_deal.length} already on this deal`);
      setLinkNotice(parts.join(' · ') || 'Nothing to link.');
      setPendingForceIds([]);
      // Re-runs the existing slaughter-rows effect so "On this deal" refreshes.
      setSlaughterLoaded(false);
    } catch (e: any) {
      if (e instanceof LinkAnimalConflict) {
        const names = e.conflicts.map((c) => `${c.eid || c.animal_id} (${c.current_deal_no || 'another deal'})`);
        setLinkError(
          `Already on another deal: ${names.join(', ')}. Linking would move them, ` +
          'which changes both deals\' counts — use “Move anyway” if that is intended.',
        );
        setPendingForceIds(animalIds);
      } else {
        setLinkError(e?.message || 'Could not link.');
      }
    } finally {
      setLinkBusy('');
    }
  };

  const applyStatus = async (next: { isCompleted?: boolean; isCancelled?: boolean }) => {
    if (!selectedDealId) return;
    setStatusSaving(true);
    setStatusError('');
    try {
      await updateDeal(selectedDealId, next);
      if (next.isCompleted !== undefined) {
        setCompletedOverride(next.isCompleted);
        onDealStatusChanged?.(selectedDealId, next.isCompleted);
        // Closing consumes an approved override; re-read so the panel and
        // the header pill show the deal as closed under it.
        loadDocRequirements();
      }
      if (next.isCancelled !== undefined) setCancelledOverride(next.isCancelled);
      setConfirmingStatus(false);
    } catch (e: any) {
      setStatusError(e?.message || 'Could not update deal status.');
    } finally {
      setStatusSaving(false);
    }
  };

  const countOf = (deal: OverviewDeal) => deal.headcount || animalSummaryByDeal[deal.dealId]?.total || 0;
  const speciesOf = (deal: OverviewDeal) => {
    if (deal.species) return speciesLabel(deal.species);
    const bySpecies = animalSummaryByDeal[deal.dealId]?.species || {};
    const top = Object.entries(bySpecies).sort((a, b) => (b[1] || 0) - (a[1] || 0))[0];
    return top ? speciesLabel(top[0]) : '';
  };

  const reqIdRef = useRef(0);
  useEffect(() => {
    // A different deal is selected — drop any pending status override/confirm
    // and return to the Dashboard tab.
    setCompletedOverride(null);
    setCancelledOverride(null);
    setConfirmingStatus(false);
    setStatusError('');
    setTab((initialTabRef.current as DealTab | undefined) || 'dashboard');
    initialTabRef.current = undefined;
    setDealAnimals([]);
    setDealAnimalsError('');
    setDealAnimalsSearch('');
    setDealAnimalsPage(1);
    setSlaughterRawRows([]);
    setSlaughterError('');
    setSlaughterLoaded(false);
    setSlaughterSearch('');
    if (!selectedDealId) {
      setTracking(null);
      setTimeline(null);
      setReport(null);
      setIntegrations(null);
      setVaccinations([]);
      setError('');
      return;
    }
    const reqId = ++reqIdRef.current;
    setLoading(true);
    setError('');
    // Each dataset is independent: the page renders as soon as tracking lands,
    // and an admin-only endpoint failing (e.g. cost report) never blanks the page.
    Promise.allSettled([
      fetchTracking(selectedDealId),
      fetchDealTimeline(selectedDealId),
      fetchDealAdminReport(selectedDealId),
      fetchDealIntegrations(selectedDealId),
      fetchDealVaccinations(selectedDealId),
    ]).then(([trk, tl, rep, integ, vacc]) => {
      if (reqId !== reqIdRef.current) return;
      if (trk.status === 'fulfilled') setTracking(trk.value);
      else {
        setTracking(null);
        setError(trk.reason?.message || 'Could not load deal tracking data.');
      }
      setTimeline(tl.status === 'fulfilled' ? tl.value : null);
      setReport(rep.status === 'fulfilled' ? rep.value : null);
      setIntegrations(integ.status === 'fulfilled' ? integ.value : null);
      setVaccinations(vacc.status === 'fulfilled' ? vacc.value : []);
      setLoading(false);
    });
  }, [selectedDealId]);

  // Feed tab: the occupancy charge, refreshed with the tab so the accrual is
  // current rather than as of whenever the page loaded.
  useEffect(() => {
    if (tab !== 'feed' || !selectedDealId) { return; }
    let cancelled = false;
    fetchFeedlotFee(selectedDealId)
      .then((data) => { if (!cancelled) setFeedlotFee(data); })
      .catch(() => { if (!cancelled) setFeedlotFee(null); });
    return () => { cancelled = true; };
  }, [tab, selectedDealId, refreshToken]);

  // Animals tab — standard list: load the deal's animals when the tab opens.
  // `retagRefresh` forces a reload after a re-tag: a merge removes the phantom
  // row, so a cached list would keep showing an animal that no longer exists.
  useEffect(() => {
    if (tab !== 'animals' || !selectedDealId) return;
    if (dealAnimals.length > 0 && retagRefresh === 0) return;
    let cancelled = false;
    setDealAnimalsLoading(true);
    setDealAnimalsError('');
    fetchAnimals(selectedDealId)
      .then((rows) => { if (!cancelled) setDealAnimals(Array.isArray(rows) ? rows : []); })
      .catch((e: any) => { if (!cancelled) setDealAnimalsError(e?.message || 'Could not load animals for this deal.'); })
      .finally(() => { if (!cancelled) setDealAnimalsLoading(false); });
    return () => { cancelled = true; };
  }, [tab, selectedDealId, dealAnimals.length, retagRefresh]);

  // Slaughter tab — pull the raw rows of every ABAT_SLA file behind this deal's
  // slaughter events. A deal can have several; a file can also carry rows that
  // never linked (see A260006 row 34), and those are exactly the ones worth
  // seeing here.
  useEffect(() => {
    if (tab !== 'slaughter' || !selectedDealId || slaughterLoaded) return;
    // `current_data_session`, not `data_session`. The event's FK points at the
    // revision ROOT for the life of the event, so fetching it renders revision
    // 1 forever -- this tab showed revision 1's 64 rows, 9 columns and
    // filename while A260006's slaughter file was on revision 4.
    const sessionIds = Array.from(new Set(
      (Array.isArray(tracking?.dealEvents) ? tracking!.dealEvents : [])
        .filter((e: any) => e?.event_type === 'ABAT_SLA' && (e?.current_data_session || e?.data_session))
        .map((e: any) => eventCurrentSessionId(e))
        .filter(Boolean),
    ));
    if (sessionIds.length === 0) { setSlaughterLoaded(true); return; }

    let cancelled = false;
    setSlaughterLoading(true);
    setSlaughterError('');
    Promise.all(sessionIds.map((id) => fetchSessionRawRows(id).catch(() => null)))
      .then((results) => {
        if (cancelled) return;
        const rows = results.filter(Boolean).flatMap((r) => r!.results);
        setSlaughterRawRows(rows);
        setSlaughterLoaded(true);
        if (rows.length === 0 && results.every((r) => r === null)) {
          setSlaughterError('Could not load the slaughter files for this deal.');
        }
      })
      .finally(() => { if (!cancelled) setSlaughterLoading(false); });
    return () => { cancelled = true; };
  }, [tab, selectedDealId, slaughterLoaded, tracking]);

  // Animals tab — standard rows (mapped + search-filtered).
  const dealAnimalRows = useMemo(() => {
    const q = dealAnimalsSearch.trim().toLowerCase();
    const rows = dealAnimals.map((a: any) => ({
      animal_id: String(a.id),
      eid: a.eid_tag || a.vid || '—',
      species: a.species ? String(a.species) : '',
      status: a.status || '',
      location: a.current_entity || a.current_gln || '',
    }));
    if (!q) return rows;
    return rows.filter((r) => [r.eid, r.species, r.status, r.location].filter(Boolean).join(' ').toLowerCase().includes(q));
  }, [dealAnimals, dealAnimalsSearch]);

  // Slaughter tab — the union of raw columns across every loaded file, so one
  // table can render files with differing layouts.
  const slaughterColumns = useMemo(() => {
    const seen = new Set<string>();
    slaughterRawRows.forEach((r) => Object.keys(r.raw_data || {}).forEach((k) => seen.add(k)));
    return Array.from(seen);
  }, [slaughterRawRows]);

  // Distinct animals behind the not-on-this-deal rows — the link targets.
  // Event types on this deal's uploads, so the unrecognised-column list shows
  // only columns that could actually appear on this deal's files.

  const unlinkedAnimalIds = useMemo(
    () => Array.from(new Set(
      slaughterRawRows
        .filter((r: any) => r.animal_id && !r.animal_deleted && r.animal_deal_id !== selectedDealId)
        .map((r: any) => String(r.animal_id)),
    )),
    [slaughterRawRows, selectedDealId],
  );

  const slaughterUnlinkedCount = useMemo(
    () => slaughterRawRows.filter((r) => !(r.animal_deal_id && r.animal_deal_id === selectedDealId)).length,
    [slaughterRawRows, selectedDealId],
  );

  // ABAT_SLA events whose file has been revised. A deal can have several
  // slaughter batches, each its own revision group, so this is a list.
  const slaughterRevisedEvents = useMemo(() => {
    const evts: any[] = Array.isArray(tracking?.dealEvents) ? tracking!.dealEvents : [];
    return evts
      .filter((e) => e?.event_type === 'ABAT_SLA' && Number(e?.revision_count || 1) > 1)
      .map((e) => ({
        event: e,
        current: Number(e.revision_number || 1),
        count: Number(e.revision_count || 1),
        revisions: (Array.isArray(e.revisions) ? e.revisions : [])
          .slice()
          .sort((a: any, b: any) => Number(b.revision_number) - Number(a.revision_number)),
      }));
  }, [tracking]);

  // Load every superseded revision's rows once, and summarise what the current
  // revision changed against each. One fetch per superseded revision, only when
  // the user opens the history -- never on tab load.
  useEffect(() => {
    if (!slaughterRevisionsOpen || slaughterRevisedEvents.length === 0) return;
    const wanted = slaughterRevisedEvents.flatMap((g) =>
      g.revisions.filter((r: any) => !r.is_current).map((r: any) => String(r.data_session)));
    if (wanted.every((id) => slaughterDiffs[id] !== undefined)) return;

    let cancelled = false;
    setSlaughterDiffsLoading(true);
    (async () => {
      const next: Record<string, string> = {};
      for (const group of slaughterRevisedEvents) {
        const head = group.revisions.find((r: any) => r.is_current);
        if (!head) continue;
        const headRows = await fetchSessionRawRows(String(head.data_session)).catch(() => null);
        if (!headRows) continue;
        for (const rev of group.revisions) {
          if (rev.is_current) continue;
          const id = String(rev.data_session);
          if (slaughterDiffs[id] !== undefined) continue;
          const older = await fetchSessionRawRows(id).catch(() => null);
          next[id] = older
            ? summariseRevisionDiff(diffRawRevisions(older.results, headRows.results))
            : 'could not be compared';
        }
      }
      if (!cancelled && Object.keys(next).length) {
        setSlaughterDiffs((prev) => ({ ...prev, ...next }));
      }
      if (!cancelled) setSlaughterDiffsLoading(false);
    })();
    return () => { cancelled = true; };
    // `slaughterDiffs` is read but deliberately not a dependency: adding it
    // would re-run this effect on every summary it stores. The guard above
    // skips revisions already compared, and a close/reopen re-runs with the
    // populated map, so nothing is fetched twice.
  }, [slaughterRevisionsOpen, slaughterRevisedEvents]);

  const slaughterFiltered = useMemo(() => {
    const q = slaughterSearch.trim().toLowerCase();
    if (!q) return slaughterRawRows;
    return slaughterRawRows.filter((r) =>
      [r.animal_eid, (r.mapped_data || {}).eid, r.original_filename, r.row_number, ...Object.values(r.raw_data || {})]
        .filter((v) => v != null && v !== '')
        .join(' ')
        .toLowerCase()
        .includes(q));
  }, [slaughterRawRows, slaughterSearch]);

  // ---- Derived tracking figures ----------------------------------------------
  const glns: any[] = Array.isArray(tracking?.glns) ? tracking!.glns : [];

  const totals = useMemo(() => {
    // Sum across ALL abattoir GLNs — a deal can carry more than one abattoir
    // entry (e.g. a placeholder "AVCS Abattoir" plus the real operator), and
    // .find() would grab the wrong one and report 0.
    const slaughtered = glns
      .filter((g) => g.type === 'Abattoir')
      .reduce((acc, g) => acc + Number(g?.events?.slaughtered || 0), 0);
    const explicitMortality = glns.reduce(
      (acc, g) => acc + Number(g?.events?.deaths || g?.events?.mortalityInTransit || 0),
      0,
    );
    const headcount = Number(tracking?.headcount || 0);
    const mortality = tracking?.total_mortalities != null ? Number(tracking.total_mortalities) : explicitMortality;
    // No `alive` figure. It was derived as headcount - mortalities - slaughtered,
    // and those come from different sources (animals on the deal vs the
    // ABAT_SLA event's count), so any disagreement between them surfaced as a
    // phantom live animal. Removed with the card it fed.
    return { headcount, mortality, slaughtered };
  }, [glns, tracking]);

  // Total days in the value chain — computed by the backend (`/timeline/`),
  // which owns the rule: deal start date → last slaughter, but only once every
  // animal has left the chain; otherwise it runs to today.
  const daysInValueChain = timeline?.total_days_in_value_chain ?? null;

  // Dressing percentage — cold carcass mass over the last live weight, averaged.
  // Computed by the backend so this tile and the financial report share one rule.
  const dressingPercentage = timeline?.dressing_percentage ?? null;
  const avgCarcassWeight = timeline?.avg_carcass_weight ?? null;
  const avgLastLiveWeight = timeline?.avg_last_live_weight ?? null;
  const gradeCounts = useMemo(() => timeline?.grade_counts ?? [], [timeline]);
  const gradeTotal = useMemo(
    () => gradeCounts.reduce((sum, g) => sum + (g.count || 0), 0),
    [gradeCounts],
  );

  // A partial basis has to be visible on every carcass figure, not just the
  // percentage: an average over 3 of 64 animals reads exactly like one over 64.
  const basisHint = (scored?: number) => {
    const total = timeline?.dressing_records_total ?? 0;
    if (!scored) return undefined;
    return scored < total ? `${scored} of ${total} records` : `Across ${scored} record(s)`;
  };
  const dressingHint = useMemo(() => {
    if (!timeline) return undefined;
    const scored = timeline.dressing_records_scored ?? 0;
    const missing = timeline.dressing_records_without_live_weight ?? 0;
    if (dressingPercentage == null) {
      return missing > 0
        ? `No live weight captured on ${missing} slaughter record(s).`
        : 'No slaughter records yet.';
    }
    // A partial basis has to be visible — otherwise an average over 3 of 62
    // animals reads exactly like an average over all 62.
    return missing > 0
      ? `${scored} of ${scored + missing} records · ${missing} with no live weight`
      : `Across ${scored} slaughter record(s)`;
  }, [timeline, dressingPercentage]);
  const daysInValueChainHint = useMemo(() => {
    if (!timeline?.total_days_start) return 'No registration event recorded yet.';
    return timeline.all_animals_out && timeline.slaughter_at
      ? `Registered ${fmtDate(timeline.total_days_start)} → slaughtered ${fmtDate(timeline.total_days_end)}`
      : `Registered ${fmtDate(timeline.total_days_start)} → today (in progress)`;
  }, [timeline]);

  // Feedlot performance shown as peer cards on the dashboard (the Feed data tab
  // keeps the fuller breakdown).
  const feedlotKpis = useMemo(() => {
    const fp = tracking?.feedlotPerformance;
    return {
      fcr: fp?.fcr != null ? Number(fp.fcr) : null,
      fcrIsEstimated: Boolean(fp?.fcrIsEstimated),
      adg: fp?.avg_adg != null ? Number(fp.avg_adg) : null,
      feedlotDays: fp?.avgFeedlotDays != null ? Number(fp.avgFeedlotDays) : null,
    };
  }, [tracking]);

  const bannerCounts = useMemo(() => {
    const sumBy = (pred: (g: any) => boolean) => glns.filter(pred).reduce((acc, g) => acc + Number(g.current || 0), 0);
    const transit = (needle: string) =>
      glns
        .filter(
          (g) =>
            g.type === 'Transit' &&
            (String(g.destinationType || '').toUpperCase().includes(needle) ||
              String(g.name || '').toUpperCase().includes(needle)),
        )
        .reduce((acc, g) => acc + Number(g.current || 0), 0);
    return {
      farm: sumBy((g) => g.type === 'Farm'),
      feedlot: sumBy((g) => g.type === 'Feedlot'),
      abattoir: sumBy((g) => g.type === 'Abattoir'),
      farmToFeedlot: transit('FEEDLOT'),
      feedlotToAbattoir: transit('ABATTOIR'),
    };
  }, [glns]);

  const documents = useMemo(() => {
    const out: Array<{ id: string; label: string; meta: string; href?: string; dealDocumentId?: string; code?: string; party?: string; financeType?: string; saleValue?: string }> = [];
    (Array.isArray(tracking?.documents) ? tracking!.documents : []).forEach((doc: any, i: number) => {
      const href = normalizeDocumentUrl(doc?.file);
      if (!href) return;
      out.push({
        id: `deal-${doc?.id || i}`,
        dealDocumentId: doc?.id ? String(doc.id) : undefined,
        code: String(doc?.document_type_code || ''),
        party: String(doc?.party || ''),
        financeType: String(doc?.finance_record_type || ''),
        saleValue: doc?.sale_value != null ? String(doc.sale_value) : undefined,
        label: doc?.document_type_name || 'Deal document',
        meta: `${doc?.reference_number ? `Ref ${doc.reference_number} · ` : ''}${fmtDate(doc?.date_of_document)}`.trim(),
        href,
      });
    });
    (Array.isArray(tracking?.transportDocuments) ? tracking!.transportDocuments : []).forEach((doc: any, i: number) => {
      const href = normalizeDocumentUrl(doc?.waybill || doc?.invoice) || undefined;
      const hasData = doc?.vehicle_registration && doc?.start_km != null && doc?.end_km != null;
      if (!href && !hasData) return;
      const meta = [
        doc?.waybill_no ? `Waybill ${doc.waybill_no}` : null,
        doc?.vehicle_registration ? `Vehicle ${doc.vehicle_registration}` : null,
        doc?.loading_date ? fmtDate(doc.loading_date) : null,
      ]
        .filter(Boolean)
        .join(' · ');
      out.push({
        id: `transport-${doc?.id || i}`,
        label: doc?.waybill ? 'Waybill' : doc?.invoice ? 'Transport invoice' : 'Transport journey',
        meta,
        href,
      });
    });
    return out;
  }, [tracking]);

  const auditEvents = useMemo(() => {
    const evts: any[] = Array.isArray(tracking?.dealEvents) ? tracking!.dealEvents : [];
    const sorted = [...evts].sort((a, b) => {
      const at = Date.parse(a?.event_date || a?.created_at || '') || 0;
      const bt = Date.parse(b?.event_date || b?.created_at || '') || 0;
      return bt - at;
    });
    if (auditFilter === 'integrations') {
      return sorted.filter((e) => e?.rmis?.status || e?.virtual_routez?.latest_call?.status || e?.virtual_routez?.payload_status);
    }
    return sorted;
  }, [tracking, auditFilter]);

  // Open the raw rows behind an event. The event carries the `data_session` of
  // the file it was built from, so the whole upload can be inspected — including
  // rows that never linked to an animal and are therefore absent from the
  // event's animal count.
  // Kept so the raw-data modal can re-fetch itself after a manual EID link.
  const [rawDataEvent, setRawDataEvent] = useState<any>(null);

  /**
   * Open the rows behind an event, or behind one specific revision of it.
   *
   * Defaults to the CURRENT revision: `evt.data_session` is the revision root
   * and is never re-pointed, so opening that showed revision 1's rows under a
   * heading that claimed to be the event's data.
   */
  const openEventRawData = async (evt: any, revisionSessionId?: string) => {
    setRawDataEvent(evt);
    const sessionId = revisionSessionId || eventCurrentSessionId(evt);
    if (!sessionId) return;
    const label = eventLabel(evt.event_type);
    const revisions: any[] = Array.isArray(evt?.revisions) ? evt.revisions : [];
    const shown = revisions.find((r) => String(r.data_session) === String(sessionId));
    const isCurrent = shown ? Boolean(shown.is_current) : true;
    // Only the current revision's stored file is offered for download; a
    // superseded revision's URL is not on the event.
    const fileUrl = isCurrent ? (normalizeDocumentUrl(evt.source_file) || undefined) : undefined;
    const revisionMeta = revisions.length > 1
      ? {
        number: shown?.revision_number ?? evt?.revision_number ?? 1,
        count: evt?.revision_count ?? revisions.length,
        isCurrent,
        uploadedAt: shown?.uploaded_at ?? null,
        // Already computed for the revision list; reused here so the banner
        // says what changed rather than only that something did.
        diffSummary: isCurrent ? undefined : slaughterDiffs[String(sessionId)],
      }
      : null;
    setRawData({
      loading: true, error: '', filename: shown?.original_filename || evt.source_filename || '', fileUrl,
      eventLabel: label, session: null, rows: [], count: 0, limit: 0, revision: revisionMeta,
    });
    try {
      const data = await fetchSessionRawRows(String(sessionId));
      setRawData({
        loading: false,
        error: '',
        filename: data.session?.original_filename || evt.source_filename || '',
        fileUrl,
        eventLabel: label,
        session: data.session,
        rows: data.results,
        count: data.count,
        limit: data.limit,
        revision: revisionMeta,
      });
    } catch (e: any) {
      setRawData((prev) => (prev ? { ...prev, loading: false, error: e?.message || 'Could not load raw data.' } : prev));
    }
  };

  // ---- Render ----------------------------------------------------------------
  return (
    <section className="min-w-0 rounded-2xl border border-[#1e6bb824] bg-white p-4 shadow-sm md:p-5">
      <div className="grid min-w-0 gap-4">
        {/* Header: title + back-to-list + status badge */}
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="text-2xl font-semibold text-avcs-text">
              {selectedDeal
                ? `${selectedDeal.title}${selectedDeal.counterpart ? ` · ${selectedDeal.counterpart}` : ''}`
                : 'Deal overview'}
            </h2>
            {selectedDeal && (
              <p className="mt-0.5 text-sm text-avcs-muted">
                {speciesOf(selectedDeal) ? `${speciesOf(selectedDeal)} · ` : ''}{fmtNum(countOf(selectedDeal))} head
              </p>
            )}
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <button className="btn" onClick={onBack}>← Back to list</button>
            {/* The step-by-step capture is still the fastest way to finish a
                deal that was started and left; it reopens on this deal. */}
            {onOpenWizard && (
              <button
                className="btn"
                disabled={!selectedDealId}
                title="Continue capturing this deal step by step"
                onClick={() => selectedDealId && onOpenWizard(selectedDealId)}
              >
                Open in wizard
              </button>
            )}
            {/* Deal-level action, so it sits in the header and stays reachable
                from every tab rather than being buried in one of them. */}
            {onNewEvent && (
              <button
                className="btn primary"
                disabled={!selectedDealId}
                onClick={() => selectedDealId && onNewEvent(selectedDealId)}
              >
                + New event
              </button>
            )}
            {selectedDeal && (
              <div className="relative">
                <button
                  type="button"
                  onClick={() => setConfirmingStatus((v) => !v)}
                  className={`inline-flex items-center gap-2 rounded-full border-2 px-4 py-2 text-base font-bold shadow-sm transition hover:shadow ${
                    isCancelled
                      ? 'border-[#e7bbb9] bg-[#f8eae7] text-[#6b1010]'
                      : isCompleted
                        ? 'border-[#c7d0d6] bg-[#eef2f5] text-[#3a4a52]'
                        : 'border-emerald-400 bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200'
                  }`}
                  aria-haspopup="dialog"
                  aria-expanded={confirmingStatus}
                  title={`Deal ${statusLabel.toLowerCase()} — click to change status`}
                >
                  <span className={`h-2.5 w-2.5 rounded-full ${isCancelled ? 'bg-[#c0392b]' : isCompleted ? 'bg-[#7b8a92]' : 'bg-emerald-500'}`} aria-hidden />
                  {statusLabel}
                  {isCompleted && !isCancelled && completedUnderOverride && (
                    <span className="rounded-full border border-[#ecd1b9] bg-[#fff7ef] px-2 py-0.5 text-[11px] font-bold uppercase tracking-wide text-[#7b4a18]" title="Closed under an approved close-out override — some requirements were waived">
                      under override
                    </span>
                  )}
                  <svg className="h-4 w-4 opacity-60" viewBox="0 0 20 20" fill="none" aria-hidden>
                    <path d="M5 7l5 5 5-5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </button>
                {confirmingStatus && (
                  <div className="absolute right-0 top-full z-40 mt-2 w-80 rounded-xl border border-[#1e6bb833] bg-white p-3 shadow-lg" role="dialog" aria-label="Change deal status">
                    {isCancelled ? (
                      <p className="text-sm text-avcs-text">
                        This deal is cancelled. Reinstating it returns it to
                        {isCompleted ? ' completed' : ' active'}; nothing was deleted.
                      </p>
                    ) : (
                      <p className="text-sm text-avcs-text">
                        {isCompleted
                          ? 'Reopen this deal? It will be marked active again.'
                          : 'Close this deal? It will be marked completed.'}
                      </p>
                    )}
                    {statusError && (
                      <div role="alert" className="mt-2 rounded-lg border border-[#e7bbb9] bg-[#f8eae7] px-2 py-1 text-xs text-[#6b1010]">{statusError}</div>
                    )}
                    <div className="mt-3 flex flex-wrap justify-end gap-2">
                      <button type="button" className="btn" onClick={() => setConfirmingStatus(false)} disabled={statusSaving}>Close</button>
                      {isCancelled ? (
                        <button type="button" className="btn primary" onClick={() => applyStatus({ isCancelled: false })} disabled={statusSaving}>
                          {statusSaving ? 'Saving…' : 'Reinstate deal'}
                        </button>
                      ) : (
                        <>
                          <button type="button" className="btn" onClick={() => applyStatus({ isCompleted: !isCompleted })} disabled={statusSaving}>
                            {statusSaving ? 'Saving…' : isCompleted ? 'Reopen deal' : 'Close deal'}
                          </button>
                          {/* Cancelling replaces deleting for admins: the deal
                              and its whole history stay auditable. */}
                          <button
                            type="button"
                            className="rounded-lg bg-[#c0392b] px-3 py-1.5 text-sm font-semibold text-white transition hover:bg-[#a93226] disabled:opacity-50"
                            onClick={() => applyStatus({ isCancelled: true })}
                            disabled={statusSaving}
                          >
                            {statusSaving ? 'Saving…' : 'Cancel deal'}
                          </button>
                        </>
                      )}
                    </div>
                    {!isCancelled && (
                      <p className="mt-2 text-xs text-avcs-muted">
                        Cancelling keeps the deal and all its data — it is not a delete.
                      </p>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {!selectedDealId ? (
          <p className="text-sm text-avcs-muted">Search and select a deal above to view its overview.</p>
        ) : loading && !tracking ? (
          <p className="text-sm text-avcs-muted">Loading deal overview…</p>
        ) : error && !tracking ? (
          <div className="rounded-lg border border-[#e7bbb9] bg-[#f8eae7] px-3 py-2 text-sm text-[#6b1010]">{error}</div>
        ) : (
          <>
            {/* Tab bar */}
            <div className="flex flex-wrap gap-1 border-b border-[#1e6bb824]">
              {DEAL_TABS.map(({ key, label }) => (
                <button
                  key={key}
                  type="button"
                  className={`-mb-px border-b-2 px-3 py-2 text-sm font-semibold transition ${tab === key ? 'border-[#1e6bb8] text-[#1e6bb8]' : 'border-transparent text-avcs-muted hover:text-avcs-text'}`}
                  onClick={() => setTab(key)}
                >
                  {label}
                </button>
              ))}
            </div>

            {tab === 'dashboard' && (
            <>
            {/* Value-chain banner */}
            <ValueChainBanner
              className="rounded-xl"
              farm={bannerCounts.farm}
              farmToFeedlot={bannerCounts.farmToFeedlot}
              feedlot={bannerCounts.feedlot}
              feedlotToAbattoir={bannerCounts.feedlotToAbattoir}
              abattoir={bannerCounts.abattoir}
              slaughtered={totals.slaughtered}
              mortalities={totals.mortality}
            />

            {/* KPI strip — headcount, lifecycle, and the deal-wide
                   outcome figures. Feedlot-only metrics live in their own
                   section below, so a deal that never saw a feedlot doesn't
                   show three empty tiles among its headline numbers. */}
            <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
              {[
                { label: 'Headcount', value: fmtNum(totals.headcount) },
                { label: 'Slaughtered', value: fmtNum(totals.slaughtered) },
                { label: 'Mortalities', value: fmtNum(totals.mortality) },
                {
                  label: 'Total days in supply chain program',
                  value: daysInValueChain != null ? fmtNum(daysInValueChain) : '—',
                  hint: daysInValueChainHint,
                },
                {
                  label: 'Dressing percentage',
                  value: dressingPercentage != null ? `${dressingPercentage.toFixed(1)}%` : '—',
                  hint: dressingHint,
                },
                {
                  label: 'Avg carcass weight',
                  value: avgCarcassWeight != null ? `${avgCarcassWeight.toFixed(1)} kg` : '—',
                  hint: basisHint(timeline?.avg_carcass_weight_count),
                },
                {
                  label: 'Avg last live weight',
                  value: avgLastLiveWeight != null ? `${avgLastLiveWeight.toFixed(1)} kg` : '—',
                  hint: basisHint(timeline?.avg_last_live_weight_count),
                },
              ].map((tile) => (
                <div key={tile.label} className="tile rounded-xl border border-[#1e6bb829] bg-white p-4">
                  <div className="tile-label">{tile.label}</div>
                  <div className="tile-value">{tile.value}</div>
                  {tile.hint && <div className="mt-1 text-xs text-avcs-muted">{tile.hint}</div>}
                </div>
              ))}
            </div>

            {/* Grade counts — a list rather than a single figure, so it gets
                   its own tile instead of being squeezed into the strip. */}
            {gradeCounts.length > 0 && (
              <div className="tile rounded-xl border border-[#1e6bb829] bg-white p-4 sm:max-w-xs">
                <div className="tile-label">Grade counts</div>
                <dl className="mt-2 grid gap-1">
                  {gradeCounts.map(({ grade, count }) => (
                    <div key={grade} className="flex items-baseline justify-between gap-3 text-sm">
                      <dt className={grade === 'Ungraded' ? 'text-avcs-muted' : 'font-semibold text-avcs-text'}>
                        {grade}
                      </dt>
                      <dd className="tabular-nums text-avcs-text">{fmtNum(count)}</dd>
                    </div>
                  ))}
                </dl>
                <div className="mt-2 border-t border-[#1e6bb814] pt-1 text-xs text-avcs-muted">
                  {fmtNum(gradeTotal)} slaughtered
                </div>
              </div>
            )}

            {/* Feedlot metrics — grouped under their own heading so it is
                   clear these describe the feedlot leg only, not the deal. */}
            <div className="rounded-xl border border-[#1e6bb824] bg-[#fbfeff] p-4">
              <div className="mb-3 text-sm font-semibold uppercase tracking-wide text-avcs-muted">
                Feedlot metrics
              </div>
              <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
                {[
                  {
                    label: 'FCR',
                    value: feedlotKpis.fcr != null
                      ? `${feedlotKpis.fcr.toFixed(2)}${feedlotKpis.fcrIsEstimated ? ' (est.)' : ''}`
                      : '—',
                  },
                  {
                    label: 'Avg daily gain',
                    value: feedlotKpis.adg != null ? `${feedlotKpis.adg.toFixed(3)} kg` : '—',
                  },
                  {
                    label: 'Feedlot days',
                    value: feedlotKpis.feedlotDays != null ? fmtNum(feedlotKpis.feedlotDays) : '—',
                  },
                ].map((tile) => (
                  <div key={tile.label} className="tile rounded-xl border border-[#1e6bb829] bg-white p-4">
                    <div className="tile-label">{tile.label}</div>
                    <div className="tile-value">{tile.value}</div>
                  </div>
                ))}
              </div>
            </div>

            </>
            )}

            {/* "Needs review" moved off the deal to its own menu screen
                (Animals → Needs review), where the queue is visible without
                opening each deal. */}

            {tab === 'events' && (
              <DealEventsSection
                dealId={selectedDealId}
                dealEvents={tracking?.dealEvents}
                glns={glns}
              />
            )}

            {tab === 'financial' && (
              <FinancialSection
                report={report}
                dealId={selectedDealId}
                finances={finances}
                onChanged={loadFinances}
              />
            )}

            {tab === 'health' && (
              <>
                {/* The declaration sits above the vaccination records: it is
                    what the seller stated about these animals, while the
                    records below are what AVCS administered. Two different
                    sources, and worth not conflating on one screen. */}
                <Section
                  title="Health declaration"
                  subtitle="As declared for this deal. An unanswered question is shown as such — it is not the same as 'no'."
                  actions={onNewVaccination && selectedDealId ? (
                    <button type="button" className="btn primary" onClick={() => onNewVaccination(selectedDealId)}>
                      New vaccination
                    </button>
                  ) : undefined}
                >
                  {dealDetail ? (
                    <DealHealthPanel
                      deal={dealDetail}
                      medications={dealMedications}
                      medicationSummary={medicationSummary}
                    />
                  ) : (
                    <p className="text-sm text-avcs-muted">
                      Select a deal to see its health declaration.
                    </p>
                  )}
                </Section>
                <HealthSection
                  vaccinations={vaccinations}
                  dealId={selectedDealId}
                  dealEvents={tracking?.dealEvents}
                  onViewRawData={openEventRawData}
                  onInvoiceSaved={() => {
                    fetchDealVaccinations(selectedDealId).then((rows) => setVaccinations(Array.isArray(rows) ? rows : [])).catch(() => {});
                    loadDocRequirements();
                    loadFinances();
                  }}
                />
              </>
            )}

            {tab === 'documents' && (() => {
              const requiredKeys = new Set(docRequirements.filter((r) => r.kind !== 'FIELD').map((r) => `${r.party || ''}|${r.code}`));
              const matchingDoc = (r: DealDocumentRequirement) =>
                documents.find((d) => d.dealDocumentId && d.code === r.code && (d.party || '') === (r.party || ''));
              const otherDocs = documents.filter((d) => !(d.dealDocumentId && requiredKeys.has(`${d.party || ''}|${d.code}`)) && d.financeType !== 'SALE');
              const revenueDocs = documents.filter((d) => d.financeType === 'SALE');
              const input = 'h-[42px] w-full rounded-lg border border-[#1e6bb833] bg-white px-3 py-2 text-sm';
              const today = new Date().toISOString().slice(0, 10);
              const refreshAfterChange = () => {
                loadDocRequirements();
                loadFinances();
                if (selectedDealId) fetchTracking(selectedDealId).then(setTracking).catch(() => {});
              };
              const fileBox = (label: string, file: File | null, onPick: (f: File | null) => void) => (
                file ? (
                  <span className="inline-flex h-[42px] items-center justify-between gap-2 rounded-lg border border-[#b9e7c9] bg-[#e7f8ee] px-3 text-sm font-semibold text-[#106b38]">
                    <span className="truncate" title={file.name}>{file.name} ✓</span>
                    <button type="button" className="text-xs font-semibold text-avcs-muted hover:text-[#6b1010]" onClick={() => onPick(null)}>Remove</button>
                  </span>
                ) : (
                  <span className="relative inline-flex h-[42px] cursor-pointer items-center justify-center rounded-lg border border-dashed border-[#1e6bb84d] bg-[#f7fbff] px-3 text-sm font-semibold text-[#0f3b64] hover:bg-[#edf5ff]">
                    {label}
                    <input type="file" className="absolute inset-0 h-full w-full cursor-pointer opacity-0" onChange={(e) => { onPick(e.target.files?.[0] || null); e.target.value = ''; }} />
                  </span>
                )
              );
              const message = docMessage && (
                <div role={docMessage.kind === 'error' ? 'alert' : 'status'} className={`mb-3 rounded-lg border px-3 py-2 text-sm ${docMessage.kind === 'error' ? 'border-[#e7bbb9] bg-[#f8eae7] text-[#6b1010]' : 'border-[#b9e7c9] bg-[#e7f8ee] text-[#106b38]'}`}>{docMessage.text}</div>
              );
              const docRow = (doc: typeof documents[number]) => (
                <div key={doc.id} className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-[#1e6bb824] bg-white px-3 py-2">
                  <div>
                    <div className="text-sm font-semibold text-avcs-text">{doc.label}{doc.saleValue ? ` · R ${Number(doc.saleValue).toLocaleString('en-ZA', { minimumFractionDigits: 2 })}` : ''}</div>
                    <div className="text-xs text-avcs-muted">{doc.meta || '—'}</div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {doc.dealDocumentId && (
                      <button type="button" className="btn" title="Book costs off this document onto events or animals" onClick={() => setCostLinesDocId(costLinesDocId === doc.dealDocumentId ? '' : doc.dealDocumentId!)}>
                        {costLinesDocId === doc.dealDocumentId ? 'Close costs' : 'Add costs'}
                      </button>
                    )}
                    {doc.href && <a className="btn" href={doc.href} target="_blank" rel="noopener noreferrer">View</a>}
                  </div>
                </div>
              );
              const costPanel = costLinesDocId && selectedDealId ? (
                <div className="mt-3">
                  <DocumentCostLinesPanel
                    key={costLinesDocId}
                    dealId={selectedDealId}
                    dealNo={selectedDeal?.title || selectedDealId}
                    headcount={selectedDeal ? countOf(selectedDeal) : 0}
                    documentId={costLinesDocId}
                    onDone={() => { setCostLinesDocId(''); refreshAfterChange(); }}
                    onCancel={() => setCostLinesDocId('')}
                  />
                </div>
              ) : null;

              const saveOther = async () => {
                if (!selectedDealId || !otherDocForm.file || docBusy) return;
                const otherType = docTypeOptions.find((o) => o.label === 'Other') || docTypeOptions.find((o) => !o.code && !o.financeRecordType);
                if (!otherType) { setDocMessage({ kind: 'error', text: 'No "Other" document type is set up.' }); return; }
                setDocBusy('other');
                setDocMessage(null);
                try {
                  await uploadDealDocument({
                    dealId: selectedDealId,
                    documentType: otherType.id,
                    file: otherDocForm.file,
                    dateOfDocument: today,
                    notes: otherDocForm.description.trim(),
                    party: '',
                    visibility: 'ALL',
                  });
                  if (otherDocForm.cost.trim() && Number(otherDocForm.cost) > 0) {
                    await createDealFinance({
                      dealId: selectedDealId,
                      recordType: 'OTHER_COST',
                      amount: otherDocForm.cost.trim(),
                      transactionDate: today,
                      description: otherDocForm.description.trim() || otherDocForm.file.name,
                    });
                  }
                  setOtherDocForm({ description: '', cost: '', file: null });
                  setDocMessage({ kind: 'ok', text: otherDocForm.cost.trim() ? 'Document filed and its cost recorded.' : 'Document filed.' });
                  refreshAfterChange();
                } catch (error: any) {
                  setDocMessage({ kind: 'error', text: error?.message || 'Could not file the document.' });
                } finally {
                  setDocBusy('');
                }
              };
              const saveRevenue = async () => {
                if (!selectedDealId || !revenueForm.file || !(Number(revenueForm.value) > 0) || docBusy) return;
                const saleType = docTypeOptions.find((o) => o.financeRecordType === 'SALE');
                if (!saleType) { setDocMessage({ kind: 'error', text: 'No document type is set up to book a sale.' }); return; }
                setDocBusy('revenue');
                setDocMessage(null);
                try {
                  await uploadDealDocument({
                    dealId: selectedDealId,
                    documentType: saleType.id,
                    file: revenueForm.file,
                    dateOfDocument: revenueForm.date || today,
                    saleValue: revenueForm.value.trim(),
                    party: '',
                    visibility: 'ALL',
                  });
                  setRevenueForm({ value: '', date: '', file: null });
                  setDocMessage({ kind: 'ok', text: 'Revenue recorded and the invoice filed.' });
                  refreshAfterChange();
                } catch (error: any) {
                  setDocMessage({ kind: 'error', text: error?.message || 'Could not record the revenue.' });
                } finally {
                  setDocBusy('');
                }
              };

              const subTabs: Array<{ key: typeof docSubTab; label: string }> = [
                { key: 'required', label: `Required (${docRequirements.filter((r) => r.kind !== 'FIELD' && r.satisfied).length}/${docRequirements.filter((r) => r.kind !== 'FIELD').length})` },
                { key: 'other', label: `Other documents${otherDocs.length ? ` (${otherDocs.length})` : ''}` },
                { key: 'revenue', label: `Revenue${revenueDocs.length ? ` (${revenueDocs.length})` : ''}` },
              ];

              return (
                <div className="grid gap-4">
                  <div className="flex flex-wrap gap-1 border-b border-[#1e6bb824]" role="tablist" aria-label="Documents">
                    {subTabs.map((t) => (
                      <button
                        key={t.key}
                        type="button"
                        role="tab"
                        aria-selected={docSubTab === t.key}
                        className={`-mb-px border-b-2 px-4 py-2 text-sm font-semibold transition ${docSubTab === t.key ? 'border-[#1e6bb8] text-[#1e6bb8]' : 'border-transparent text-avcs-muted hover:text-avcs-text'}`}
                        onClick={() => { setDocSubTab(t.key); setDocMessage(null); setCostLinesDocId(''); }}
                      >
                        {t.label}
                      </button>
                    ))}
                  </div>

                  {docSubTab === 'required' && (
                    <Section
                      title="Required documents"
                      subtitle="What this deal still owes. A deal cannot be closed while any are outstanding, unless a superuser approves an override."
                    >
                      <DealDocumentChecklist
                        requirements={docRequirements}
                        canComplete={canCompleteDeal}
                        isCompleted={isCompleted && !isCancelled}
                        linkFor={(r) => {
                          const doc = matchingDoc(r);
                          return doc ? { href: doc.href, dealDocumentId: doc.dealDocumentId } : null;
                        }}
                        costsOpenId={costLinesDocId}
                        onToggleCosts={(id) => setCostLinesDocId(costLinesDocId === id ? '' : id)}
                        below={costPanel}
                      />
                      <div className="mt-4">
                        <DealCompletionOverridePanel
                          override={completionOverride}
                          canComplete={canCompleteDeal}
                          isCompleted={isCompleted}
                          isCancelled={isCancelled}
                          onRequest={requestOverride}
                        />
                      </div>
                    </Section>
                  )}

                  {docSubTab === 'other' && (
                    <>
                      <Section title="Other documents" subtitle="Not required to close the deal, but worth keeping: agreements, reports, transport papers.">
                        {otherDocs.length === 0 ? (
                          <p className="text-sm text-avcs-muted">Nothing filed here yet.</p>
                        ) : (
                          <div className="grid gap-2">{otherDocs.map(docRow)}</div>
                        )}
                        {costPanel}
                      </Section>
                      <Section title="Add a document" subtitle="A description, the file and, if it has one, its cost (excl VAT).">
                        {message}
                        <div className="grid items-start gap-x-4 gap-y-2 md:grid-cols-[minmax(0,2fr)_minmax(0,1fr)_minmax(0,1.2fr)_auto]">
                          <label className="grid content-start gap-1 text-sm text-avcs-text">
                            Description
                            <input className={input} value={otherDocForm.description} onChange={(e) => setOtherDocForm((f) => ({ ...f, description: e.target.value }))} />
                          </label>
                          <label className="grid content-start gap-1 text-sm text-avcs-text">
                            Cost (excl VAT)
                            <input type="number" min="0" step="0.01" className={input} value={otherDocForm.cost} onChange={(e) => setOtherDocForm((f) => ({ ...f, cost: e.target.value }))} />
                          </label>
                          <div className="grid content-start gap-1 text-sm text-avcs-text">
                            File
                            {fileBox('Choose the file', otherDocForm.file, (file) => setOtherDocForm((f) => ({ ...f, file })))}
                          </div>
                          <div className="grid content-start gap-1 text-sm">
                            <span aria-hidden="true">&nbsp;</span>
                            <button type="button" className="btn primary" style={{ height: 42 }} disabled={!otherDocForm.file || docBusy === 'other'} onClick={saveOther}>
                              {docBusy === 'other' ? 'Saving…' : 'Add document'}
                            </button>
                          </div>
                        </div>
                      </Section>
                      {/* One invoice that covered several deals' events is money on each of them. */}
                      <details className="rounded-xl border border-[#1e6bb824] bg-[#fbfeff] p-4">
                        <summary className="cursor-pointer text-sm font-semibold text-avcs-text">Costs on events (one invoice for several deals)</summary>
                        <div className="mt-3">
                          <SharedFeedlotCosts dealId={selectedDealId} dealLabel={selectedDeal?.title} refreshToken={refreshToken} />
                        </div>
                      </details>
                    </>
                  )}

                  {docSubTab === 'revenue' && (
                    <>
                      <Section title="Revenue" subtitle="Sale invoices and settlements. The value is booked to the deal as revenue.">
                        {revenueDocs.length === 0 ? (
                          <p className="text-sm text-avcs-muted">No revenue recorded yet.</p>
                        ) : (
                          <div className="grid gap-2">{revenueDocs.map(docRow)}</div>
                        )}
                      </Section>
                      <Section title="Add revenue" subtitle="Upload the invoice and enter what it paid, excluding VAT.">
                        {message}
                        <div className="grid items-start gap-x-4 gap-y-2 md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_minmax(0,1.4fr)_auto]">
                          <label className="grid content-start gap-1 text-sm text-avcs-text">
                            Sale value (excl VAT)
                            <input type="number" min="0" step="0.01" className={input} value={revenueForm.value} onChange={(e) => setRevenueForm((f) => ({ ...f, value: e.target.value }))} />
                          </label>
                          <label className="grid content-start gap-1 text-sm text-avcs-text">
                            Invoice date
                            <input type="date" className={input} value={revenueForm.date || today} onChange={(e) => setRevenueForm((f) => ({ ...f, date: e.target.value }))} />
                          </label>
                          <div className="grid content-start gap-1 text-sm text-avcs-text">
                            Invoice or settlement
                            {fileBox('Choose the file', revenueForm.file, (file) => setRevenueForm((f) => ({ ...f, file })))}
                          </div>
                          <div className="grid content-start gap-1 text-sm">
                            <span aria-hidden="true">&nbsp;</span>
                            <button type="button" className="btn primary" style={{ height: 42 }} disabled={!revenueForm.file || !(Number(revenueForm.value) > 0) || docBusy === 'revenue'} onClick={saveRevenue}>
                              {docBusy === 'revenue' ? 'Saving…' : 'Save revenue'}
                            </button>
                          </div>
                        </div>
                      </Section>
                    </>
                  )}
                </div>
              );
            })()}

            {tab === 'integrations' && <IntegrationsSection integrations={integrations} />}

            {tab === 'audit' && (
            <Section
              title="Audit trail"
              subtitle="Every recorded event for this deal, with RMIS and Virtual Routez sync status."
              actions={
                <div className="inline-flex overflow-hidden rounded-lg border border-[#1e6bb833] text-xs font-semibold">
                  {(['all', 'integrations'] as const).map((key) => (
                    <button
                      key={key}
                      type="button"
                      className={`px-3 py-1.5 ${auditFilter === key ? 'bg-[#1e6bb8] text-white' : 'bg-white text-avcs-muted hover:bg-[#edf5ff]'}`}
                      onClick={() => setAuditFilter(key)}
                    >
                      {key === 'all' ? 'All events' : 'Integration activity'}
                    </button>
                  ))}
                </div>
              }
            >
              {auditEvents.length === 0 ? (
                <p className="text-sm text-avcs-muted">
                  {auditFilter === 'integrations' ? 'No integration activity recorded for this deal.' : 'No events recorded for this deal.'}
                </p>
              ) : (
                <div className="admin-table-scroll tab-fill-scroll overflow-x-auto">
                  <table className="min-w-full border-collapse">
                    <thead>
                      <tr>
                        {/* The numeric columns read left to right as what
                            happened to the upload: rows in the file, how many
                            became animals, then the EID split of those. */}
                        {['Event', 'From → To', 'Date', 'Rows in file', 'Total animals', 'With EID', 'No EID', 'Source file', 'RMIS', 'Virtual Routez'].map((h, i) => (
                          <th
                            key={h}
                            className={`border-b border-[#1e6bb824] px-3 py-2 text-xs font-semibold uppercase tracking-wide text-avcs-muted ${i >= 3 && i <= 6 ? 'text-right' : 'text-left'}`}
                          >
                            {h}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {auditEvents.map((evt: any) => {
                        const from = evt.source_entity_name || '—';
                        const to = evt.destination_entity_name || '—';
                        const vrzStatus = evt?.virtual_routez?.latest_call?.status || evt?.virtual_routez?.payload_status || null;
                        const sourceHref = normalizeDocumentUrl(evt.source_file);
                        const { rowsInFile, total: totalAnimals, withEid, withoutEid, droppedRows } =
                          auditCounts(evt);
                        return (
                          <tr key={evt.id}>
                            <td className="border-b border-[#1e6bb814] px-3 py-2 text-sm text-avcs-text">{eventLabel(evt.event_type)}</td>
                            <td className="border-b border-[#1e6bb814] px-3 py-2 text-sm text-avcs-text">
                              {from === '—' && to === '—' ? '—' : `${from} → ${to}`}
                            </td>
                            <td className="border-b border-[#1e6bb814] px-3 py-2 text-sm text-avcs-muted">{fmtDate(evt.event_date || evt.created_at)}</td>
                            {/* Rows in file, flagged when some of them never
                                became animals — the drop is the interesting
                                part, so it is called out rather than left to be
                                spotted by comparing two columns. */}
                            <td className="border-b border-[#1e6bb814] px-3 py-2 text-right text-sm">
                              {rowsInFile == null ? (
                                <span className="text-avcs-muted">—</span>
                              ) : droppedRows > 0 ? (
                                <span
                                  className="font-semibold text-[#b45309]"
                                  title={`${fmtNum(droppedRows)} of ${fmtNum(rowsInFile)} row${rowsInFile === 1 ? '' : 's'} in this file did not match an animal, so they are not counted in the figures to the right. Use "View raw data" to see which.`}
                                >
                                  {fmtNum(rowsInFile)}
                                </span>
                              ) : (
                                <span className="text-avcs-text">{fmtNum(rowsInFile)}</span>
                              )}
                            </td>
                            {/* Total / with-EID / no-EID come from one backend
                                source per event, so they always add up. Falls
                                back to the event's own animal_count for a total
                                where the split isn't available. */}
                            <td className="border-b border-[#1e6bb814] px-3 py-2 text-right text-sm text-avcs-text">
                              {totalAnimals != null ? fmtNum(totalAnimals) : '—'}
                            </td>
                            <td className="border-b border-[#1e6bb814] px-3 py-2 text-right text-sm text-avcs-text">
                              {withEid != null ? fmtNum(withEid) : '—'}
                            </td>
                            <td className="border-b border-[#1e6bb814] px-3 py-2 text-right text-sm text-avcs-text">
                              {withoutEid != null ? fmtNum(withoutEid) : '—'}
                            </td>
                            {/* Source file: download the original upload, and drill into its
                                rows to trace what ingest made of each one. */}
                            <td className="border-b border-[#1e6bb814] px-3 py-2 text-sm">
                              {sourceHref || evt.data_session ? (
                                <div className="flex flex-col gap-0.5">
                                  {sourceHref ? (
                                    <a
                                      className="max-w-64 truncate font-mono text-xs text-[#1e6bb8] hover:underline"
                                      href={sourceHref}
                                      target="_blank"
                                      rel="noreferrer"
                                      title={evt.source_filename || 'Download source file'}
                                    >
                                      {evt.source_filename || 'Source file'}
                                    </a>
                                  ) : (
                                    // No stored file: e.g. a receive created by the
                                    // dispatch->receipt connector rather than an upload.
                                    <span className="max-w-64 truncate text-xs text-avcs-muted" title={evt.source_filename || 'No uploaded file for this event'}>
                                      {evt.source_filename || 'No source file'}
                                    </span>
                                  )}
                                  {/* Say when the file behind this event has
                                      been revised -- the filename above is the
                                      current revision's, which otherwise reads
                                      as the only upload there ever was. */}
                                  {Number(evt.revision_count || 1) > 1 && (
                                    <span
                                      className="self-start rounded-full border border-[#e8c9a6] bg-[#fdf3e7] px-2 py-0.5 text-[11px] font-semibold text-[#8a4b12]"
                                      title={`This file has been re-uploaded. The deal's figures come from revision ${evt.revision_number}.`}
                                    >
                                      rev {evt.revision_number} of {evt.revision_count}
                                    </span>
                                  )}
                                  {evt.data_session && (
                                    <button
                                      type="button"
                                      className="self-start text-xs font-semibold text-[#1e6bb8] hover:underline"
                                      onClick={() => openEventRawData(evt)}
                                    >
                                      View raw data
                                    </button>
                                  )}
                                </div>
                              ) : (
                                <span className="text-xs text-avcs-muted">—</span>
                              )}
                            </td>
                            <td className="border-b border-[#1e6bb814] px-3 py-2">
                              {evt?.rmis?.status ? <StatusPill status={evt.rmis.status} /> : <span className="text-xs text-avcs-muted">N/A</span>}
                            </td>
                            <td className="border-b border-[#1e6bb814] px-3 py-2">
                              {vrzStatus ? <StatusPill status={vrzStatus} /> : <span className="text-xs text-avcs-muted">N/A</span>}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </Section>
            )}

            {tab === 'details' && (
              !selectedDeal ? (
                <Section title="Deal details" subtitle="Core information for this deal.">
                  <p className="text-sm text-avcs-muted">Deal details unavailable.</p>
                </Section>
              ) : detailsEditing ? (
                <Section
                  title="Edit deal details"
                  subtitle="Seller and purchaser identity live on their entity screens; everything else about the deal is editable here."
                  actions={
                    <button type="button" className="btn" disabled={detailsSaving} onClick={() => setDetailsEditing(false)}>
                      Cancel
                    </button>
                  }
                >
                  {(() => {
                    const f = detailsForm;
                    const set = (key: string, value: string) => setDetailsForm((prev) => ({ ...prev, [key]: value }));
                    const input = (key: string, label: string, type: 'text' | 'number' | 'date' = 'text', extra: Record<string, any> = {}) => (
                      <label key={key} className="grid gap-1 text-sm text-avcs-text">
                        {label}
                        <input
                          type={type}
                          className="rounded-lg border border-[#1e6bb833] px-3 py-2"
                          value={f[key] ?? ''}
                          onChange={(e) => set(key, e.target.value)}
                          {...extra}
                        />
                      </label>
                    );
                    const numOrNull = (v: string) => (v.trim() === '' ? null : Number(v));
                    const strOrNull = (v: string) => (v.trim() === '' ? null : v.trim());
                    const save = async () => {
                      if (!selectedDealId) return;
                      setDetailsSaving(true);
                      setDetailsError('');
                      try {
                        await updateDeal(selectedDealId, {
                          dealReferenceNo: f.dealReferenceNo.trim(),
                          dealDate: f.dealDate,
                          totalAnimals: numOrNull(f.totalAnimals) ?? 0,
                          totalMale: numOrNull(f.totalMale),
                          totalFemale: numOrNull(f.totalFemale),
                          identificationType: f.identificationType.trim(),
                          totalWeightKg: strOrNull(f.totalWeightKg),
                          priceMale: strOrNull(f.priceMale),
                          priceFemale: strOrNull(f.priceFemale),
                          totalPriceExVat: strOrNull(f.totalPriceExVat),
                          totalPriceInclVat: strOrNull(f.totalPriceInclVat),
                          penaltyTerms: f.penaltyTerms,
                          origin: f.origin.trim(),
                          purchaseChannel: f.purchaseChannel.trim(),
                          traceabilityDocumented: f.traceabilityDocumented === 'yes',
                          sellerAgentName: f.sellerAgentName.trim(),
                          sellerAgentContact: f.sellerAgentContact.trim(),
                          purchaserAgentName: f.purchaserAgentName.trim(),
                          purchaserAgentContact: f.purchaserAgentContact.trim(),
                        });
                        const fresh = await fetchDealById(selectedDealId);
                        setDealDetail(fresh);
                        setDetailsEditing(false);
                      } catch (e: any) {
                        setDetailsError(e?.message || 'Could not save the deal details.');
                      } finally {
                        setDetailsSaving(false);
                      }
                    };
                    const codeOptions = Object.entries(CAPTURE_CODE_LABELS);
                    const codeSelect = (key: string, label: string) => (
                      <label key={key} className="grid gap-1 text-sm text-avcs-text">
                        {label}
                        <input
                          list={`deal-detail-codes-${key}`}
                          className="rounded-lg border border-[#1e6bb833] px-3 py-2"
                          value={f[key] ?? ''}
                          onChange={(e) => set(key, e.target.value)}
                        />
                        <datalist id={`deal-detail-codes-${key}`}>
                          {codeOptions.map(([code, text]) => <option key={code} value={code}>{text}</option>)}
                        </datalist>
                      </label>
                    );
                    return (
                      <form
                        className="grid gap-4"
                        onSubmit={(e) => { e.preventDefault(); save(); }}
                      >
                        {detailsError && (
                          <div className="rounded-lg border border-[#e7bbb9] bg-[#f8eae7] px-3 py-2 text-sm text-[#6b1010]">{detailsError}</div>
                        )}
                        <div>
                          <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-avcs-muted">Transaction</p>
                          <div className="grid gap-3 md:grid-cols-2">
                            {input('dealReferenceNo', 'Reference')}
                            {input('dealDate', 'Deal date', 'date')}
                          </div>
                        </div>
                        <div>
                          <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-avcs-muted">Agents</p>
                          <div className="grid gap-3 md:grid-cols-2">
                            {input('sellerAgentName', 'Seller agent')}
                            {input('sellerAgentContact', 'Seller agent contact')}
                            {input('purchaserAgentName', 'Purchaser agent')}
                            {input('purchaserAgentContact', 'Purchaser agent contact')}
                          </div>
                        </div>
                        <div>
                          <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-avcs-muted">Animals &amp; pricing</p>
                          <div className="grid gap-3 md:grid-cols-2">
                            {input('totalAnimals', 'Quantity total', 'number', { min: 0 })}
                            {codeSelect('identificationType', 'Identification type')}
                            {input('totalMale', 'Male', 'number', { min: 0 })}
                            {input('totalFemale', 'Female', 'number', { min: 0 })}
                            {input('totalWeightKg', 'Total weight (kg)', 'number', { min: 0, step: '0.01' })}
                            {input('priceMale', 'Price — male (R)', 'number', { min: 0, step: '0.01' })}
                            {input('priceFemale', 'Price — female (R)', 'number', { min: 0, step: '0.01' })}
                            {input('totalPriceExVat', 'Total ex VAT (R)', 'number', { min: 0, step: '0.01' })}
                            {input('totalPriceInclVat', 'Total incl VAT (R)', 'number', { min: 0, step: '0.01' })}
                          </div>
                          <label className="mt-3 grid gap-1 text-sm text-avcs-text">
                            Penalty terms
                            <textarea
                              className="rounded-lg border border-[#1e6bb833] px-3 py-2"
                              rows={2}
                              value={f.penaltyTerms ?? ''}
                              onChange={(e) => set('penaltyTerms', e.target.value)}
                            />
                          </label>
                        </div>
                        <div>
                          <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-avcs-muted">Origin &amp; traceability</p>
                          <div className="grid gap-3 md:grid-cols-2">
                            {codeSelect('origin', 'Purchased or born on farm')}
                            {codeSelect('purchaseChannel', 'Purchase channel')}
                            <label className="grid gap-1 text-sm text-avcs-text">
                              Full traceability documentation in place
                              <select
                                className="rounded-lg border border-[#1e6bb833] px-3 py-2"
                                value={f.traceabilityDocumented ?? 'no'}
                                onChange={(e) => set('traceabilityDocumented', e.target.value)}
                              >
                                <option value="no">No</option>
                                <option value="yes">Yes</option>
                              </select>
                            </label>
                          </div>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          <button type="submit" className="btn primary" disabled={detailsSaving}>
                            {detailsSaving ? 'Saving…' : 'Save deal details'}
                          </button>
                          <button type="button" className="btn" disabled={detailsSaving} onClick={() => setDetailsEditing(false)}>
                            Cancel
                          </button>
                        </div>
                      </form>
                    );
                  })()}
                </Section>
              ) : (
              <>
                {/* One row of sub-tabs, so the tab opens on what was asked
                    for rather than a five-section scroll. */}
                <div className="flex items-end justify-between gap-3 border-b border-[#1e6bb824]">
                  <div className="admin-table-scroll min-w-0 overflow-x-auto">
                    <div className="flex min-w-max items-center gap-1">
                      {DETAILS_SUB_TABS.map(({ key, label }) => {
                      const active = detailsSubTab === key;
                      return (
                        <button
                          key={key}
                          type="button"
                          className={`-mb-px border-b-2 px-3 py-2 text-sm font-semibold transition ${
                            active
                              ? 'border-[#1e6bb8] text-[#1e6bb8]'
                              : 'border-transparent text-avcs-muted hover:text-avcs-text'
                          }`}
                          aria-current={active ? 'page' : undefined}
                          onClick={() => setDetailsSubTab(key)}
                        >
                            {label}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                  {/* Sits on the tab row rather than above it: one line of
                      chrome instead of two. */}
                  <button
                    type="button"
                    className="btn mb-1 shrink-0"
                    disabled={!dealDetail}
                    onClick={() => beginDetailsEdit(dealDetail)}
                  >
                    Edit deal details
                  </button>
                </div>

                {detailsSubTab === 'ownership' && selectedDealId && (
                  <OwnershipFlow dealId={selectedDealId} refreshToken={refreshToken} />
                )}

                {detailsSubTab === 'transaction' && (
                <Section
                  title="Transaction"
                  subtitle="The deal itself — its number, programme and status."
                >
                  <DetailRows rows={[
                    ['Deal number', selectedDeal.title || '—'],
                    ['Reference', selectedDeal.dealRefNo || selectedDeal.deal_reference_no || '—'],
                    ['AVCS SCP deal', dealDetail ? (dealDetail.is_scp_deal ? 'Yes' : 'No — Deal Matrix') : '—'],
                    ['Supply Chain Program', dealDetail?.scp_name || dealDetail?.value_chain?.name || (dealDetail ? 'Deal Matrix' : '—')],
                    ['Deal date', fmtDate(dealDetail?.deal_date || selectedDeal.startDate)],
                    ['Status', statusLabel],
                    ['Species', speciesOf(selectedDeal) || '—'],
                    ['Headcount', fmtNum(countOf(selectedDeal))],
                  ]} />
                </Section>
                )}

                {detailsSubTab === 'seller' && (
                <Section
                  title="Seller"
                  subtitle="The primary producer's details as captured for this deal."
                >
                  {dealDetail?.primary_producer ? (
                    <>
                      <DetailRows rows={partyRows(dealDetail.primary_producer)} />
                      <div className="mt-3">
                        <DetailRows rows={[
                          ['Agent for seller', dealDetail.seller_agent_name ? 'Yes' : 'No'],
                          ...(dealDetail.seller_agent_name
                            ? [
                                ['Agent name', dealDetail.seller_agent_name] as [string, React.ReactNode],
                                ['Agent contact', dealDetail.seller_agent_contact || '—'] as [string, React.ReactNode],
                              ]
                            : []),
                        ]} />
                      </div>
                    </>
                  ) : (
                    <p className="text-sm text-avcs-muted">No seller captured.</p>
                  )}
                </Section>
                )}

                {detailsSubTab === 'purchaser' && (
                <Section
                  title="Purchaser"
                  subtitle="The purchasing party's details as captured for this deal."
                >
                  {dealDetail?.purchaser ? (
                    <>
                      <DetailRows rows={partyRows(dealDetail.purchaser, { vat: false })} />
                      <div className="mt-3">
                        <DetailRows rows={[
                          ['Agent for purchaser', dealDetail.purchaser_agent_name ? 'Yes' : 'No'],
                          ...(dealDetail.purchaser_agent_name
                            ? [
                                ['Agent name', dealDetail.purchaser_agent_name] as [string, React.ReactNode],
                                ['Agent contact', dealDetail.purchaser_agent_contact || '—'] as [string, React.ReactNode],
                              ]
                            : []),
                        ]} />
                      </div>
                    </>
                  ) : (
                    <p className="text-sm text-avcs-muted">No purchaser captured yet.</p>
                  )}
                </Section>
                )}

                {detailsSubTab === 'animals' && (
                <Section
                  title="Animals & pricing"
                  subtitle="The declared numbers this deal was captured with — the live per-animal list is on the Animals tab."
                >
                  {dealDetail ? (
                    <DetailRows rows={[
                      ['Quantity total', dealDetail.total_animals != null ? fmtNum(dealDetail.total_animals) : '—'],
                      ['Male', dealDetail.total_male != null ? fmtNum(dealDetail.total_male) : '—'],
                      ['Female', dealDetail.total_female != null ? fmtNum(dealDetail.total_female) : '—'],
                      ['Identification type', dealDetail.identification_type ? captureLabel(dealDetail.identification_type) : 'Not captured'],
                      ['Total weight', dealDetail.total_weight_kg != null && dealDetail.total_weight_kg !== '' ? `${fmtNum(Number(dealDetail.total_weight_kg))} kg` : '—'],
                      ['Average weight per animal', dealDetail.average_weight_kg != null ? `${fmtNum(Number(dealDetail.average_weight_kg))} kg` : '—'],
                      ['Price — male', dealDetail.price_male != null && dealDetail.price_male !== '' ? `R ${fmtNum(Number(dealDetail.price_male))}` : '—'],
                      ['Price — female', dealDetail.price_female != null && dealDetail.price_female !== '' ? `R ${fmtNum(Number(dealDetail.price_female))}` : '—'],
                      ['Total (ex VAT)', dealDetail.total_price_ex_vat != null && dealDetail.total_price_ex_vat !== '' ? `R ${fmtNum(Number(dealDetail.total_price_ex_vat))}` : '—'],
                      ['Total (incl VAT)', dealDetail.total_price_incl_vat != null && dealDetail.total_price_incl_vat !== '' ? `R ${fmtNum(Number(dealDetail.total_price_incl_vat))}` : '—'],
                      ['Penalty terms', dealDetail.penalty_terms || '—'],
                    ]} />
                  ) : (
                    <p className="text-sm text-avcs-muted">Loading deal detail…</p>
                  )}
                </Section>
                )}

                {detailsSubTab === 'origin' && (
                <Section
                  title="Origin & traceability"
                  subtitle="Where the animals came from, as declared."
                >
                  {dealDetail ? (
                    <DetailRows rows={[
                      ['Purchased or born on farm', dealDetail.origin ? captureLabel(dealDetail.origin) : 'Not captured'],
                      ...(dealDetail.origin === 'PURCHASED'
                        ? [['Purchase channel', dealDetail.purchase_channel ? captureLabel(dealDetail.purchase_channel) : 'Not captured'] as [string, React.ReactNode]]
                        : []),
                      ['Full traceability documentation in place', dealDetail.traceability_documented === true ? 'Yes' : 'No'],
                    ]} />
                  ) : (
                    <p className="text-sm text-avcs-muted">Loading deal detail…</p>
                  )}
                </Section>
                )}
              </>
              )
            )}

            {tab === 'transport' && (
            <Section
              title="Transport journeys"
              subtitle="Every journey recorded on this deal, with the transporter, vehicle and Stock Theft Act removal certificate."
              actions={onNewJourney && selectedDealId ? (
                <button type="button" className="btn primary" onClick={() => onNewJourney(selectedDealId)}>
                  New journey
                </button>
              ) : undefined}
            >
              {dealJourneys.length === 0 ? (
                <p className="text-sm text-avcs-muted">No journeys recorded for this deal yet.</p>
              ) : (
                <div className="grid gap-3">
                  {dealJourneys.map((journey) => (
                    <div key={journey.id} className="rounded-xl border border-[#1e6bb824] bg-white p-3">
                      <div className="mb-2 flex flex-wrap items-center gap-2">
                        <span className="text-sm font-semibold text-avcs-text">
                          {journey.loadingDate ? fmtDate(journey.loadingDate) : 'Journey'}
                          {journey.loadingTime ? ` · ${String(journey.loadingTime).slice(0, 5)}` : ''}
                        </span>
                        {(journey.loadingPoint || journey.offloadingPoint) && (
                          <span className="text-xs text-avcs-muted">
                            {[journey.loadingPoint, journey.offloadingPoint].filter(Boolean).join(' → ')}
                          </span>
                        )}
                        {journey.removalCertificateUrl ? (
                          <a
                            className="ml-auto rounded-full border border-[#b9e7c9] bg-[#e7f8ee] px-2.5 py-0.5 text-xs font-semibold text-[#106b38]"
                            href={journey.removalCertificateUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            Removal certificate ✓
                          </a>
                        ) : (
                          <span className="ml-auto rounded-full border border-[#ecd1b9] bg-[#fff7ef] px-2.5 py-0.5 text-xs font-semibold text-[#7b4a18]">
                            Removal certificate missing
                          </span>
                        )}
                      </div>
                      <DetailRows rows={[
                        ['Loading point', journey.loadingPoint || '—'],
                        ['Loading date', journey.loadingDate ? fmtDate(journey.loadingDate) : '—'],
                        ['Loading time', journey.loadingTime ? String(journey.loadingTime).slice(0, 5) : '—'],
                        ['Driver', [journey.driverName, journey.driverContact].filter(Boolean).join(' · ') || '—'],
                        ['Truck registration', journey.vehicleRegistration || '—'],
                        ['Begin km reading', journey.startKm != null ? fmtNum(journey.startKm) : '—'],
                        ['End km reading', journey.endKm != null ? fmtNum(journey.endKm) : '—'],
                        ['Drop-off point', journey.offloadingPoint || '—'],
                        ['Animals down / DOA', journey.animalsDown != null ? fmtNum(journey.animalsDown) : '0'],
                        ...(journey.animalsDownReason
                          ? [['Reason for injury / DOA', journey.animalsDownReason] as [string, React.ReactNode]]
                          : []),
                        ['Waybill no', journey.waybillNo || '—'],
                        ['Journey cost', journey.totalCost != null ? `R ${fmtNum(journey.totalCost)}` : '—'],
                        ['Documents', (journey.waybillUrl || journey.invoiceUrl || journey.removalCertificateUrl) ? (
                          <span className="flex flex-wrap justify-end gap-1.5">
                            {journey.waybillUrl && <a className="btn admin-inline-action" href={normalizeDocumentUrl(journey.waybillUrl)} target="_blank" rel="noopener noreferrer">View waybill</a>}
                            {journey.invoiceUrl && <a className="btn admin-inline-action" href={normalizeDocumentUrl(journey.invoiceUrl)} target="_blank" rel="noopener noreferrer">View invoice</a>}
                            {journey.removalCertificateUrl && <a className="btn admin-inline-action" href={normalizeDocumentUrl(journey.removalCertificateUrl)} target="_blank" rel="noopener noreferrer">View removal certificate</a>}
                          </span>
                        ) : 'No documents attached'],
                      ]} />
                    </div>
                  ))}
                </div>
              )}
            </Section>
            )}

            {tab === 'costModel' && (
            <Section
              title="Margin analysis"
              subtitle="What each animal cost, what it earned and what was left, from the money actually recorded on this deal. Open a line's sources to see the invoices behind it."
            >
              <DealCostModel dealId={selectedDealId} refreshToken={refreshToken} />
            </Section>
            )}

            {tab === 'feed' && (
            <Section
              title="Feed data"
              subtitle="Feedlot performance for this deal. Feed bills are captured under Event Management → Add feed and reach the animals when the deal closes."
              actions={<button className="btn primary" disabled={!selectedDealId || !onAddFeed} onClick={() => selectedDealId && onAddFeed && onAddFeed(selectedDealId)}>Add feed</button>}
            >
              {/* The feedlot's own charge. Shown before dispatch as well as
                  after, because the rate is configured and the money is real
                  whether or not an invoice has arrived yet. */}
              {feedlotFee && (feedlotFee.total > 0 || feedlotFee.accruingFeedlots.length > 0) && (
                <div className="mb-4 rounded-xl border border-[#1e6bb824] bg-white p-3">
                  <div className="mb-2 flex flex-wrap items-baseline justify-between gap-2">
                    <div className="text-sm font-semibold text-avcs-text">Feedlot occupancy fee</div>
                    <div className="text-sm font-semibold tabular-nums text-avcs-text">{fmtMoney(feedlotFee.total)}</div>
                  </div>
                  <div className="grid gap-3 sm:grid-cols-2">
                    <div className="rounded-lg border border-[#1e6bb829] bg-[#fbfeff] p-3">
                      <div className="text-[11px] uppercase tracking-wide text-avcs-muted">Booked</div>
                      <div className="mt-0.5 text-lg font-semibold tabular-nums text-avcs-text">{fmtMoney(feedlotFee.bookedTotal)}</div>
                      <div className="mt-0.5 text-xs text-avcs-muted">
                        {feedlotFee.booked.length === 0
                          ? 'nothing dispatched off a feedlot yet'
                          : `${feedlotFee.booked.length} dispatch${feedlotFee.booked.length === 1 ? '' : 'es'}, on the ledger`}
                      </div>
                    </div>
                    <div className="rounded-lg border border-dashed border-[#e0c48a] bg-[#fdf6e7] p-3">
                      <div className="text-[11px] uppercase tracking-wide text-[#5c4300]">Still accruing</div>
                      <div className="mt-0.5 text-lg font-semibold tabular-nums text-[#5c4300]">{fmtMoney(feedlotFee.accruingTotal)}</div>
                      <div className="mt-0.5 text-xs text-[#5c4300]">
                        {feedlotFee.accruingAnimalCount === 0
                          ? 'no animals still at a feedlot'
                          : `${fmtNum(feedlotFee.accruingAnimalCount)} animal(s) on site · ${fmtNum(feedlotFee.accruingAnimalDays)} animal-days as of ${fmtDate(feedlotFee.asOf)}`}
                      </div>
                    </div>
                  </div>
                  {feedlotFee.accruingFeedlots.length > 0 && (
                    <ul className="mt-2 grid gap-1">
                      {feedlotFee.accruingFeedlots.map((row) => (
                        <li key={row.entityId} className="flex flex-wrap items-center justify-between gap-2 text-xs">
                          <span className="text-avcs-text">
                            <span className="font-medium">{row.entityName}</span>
                            <span className="text-avcs-muted">
                              {` · ${fmtMoney(row.ratePerDay)} per animal per day · ${fmtNum(row.animalCount)} on site · ${fmtNum(row.animalDays)} animal-days`}
                            </span>
                          </span>
                          <span className="font-semibold tabular-nums text-avcs-text">{fmtMoney(row.amount)}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                  {feedlotFee.booked.length > 0 && (
                    <ul className="mt-2 grid gap-1 border-t border-[#1e6bb814] pt-2">
                      {feedlotFee.booked.map((row) => (
                        <li key={row.id} className="flex flex-wrap items-center justify-between gap-2 text-xs">
                          <span className="min-w-0 text-avcs-muted">
                            <span className="font-medium text-avcs-text">{fmtDate(row.transactionDate)}</span>
                            {row.description ? ` · ${row.description}` : ''}
                          </span>
                          <span className="font-semibold tabular-nums text-avcs-text">{fmtMoney(row.amount)}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
              {(() => {
                const fp = tracking?.feedlotPerformance;
                const hasData = fp && (fp.fcr != null || fp.avg_adg != null || fp.avgFeedlotDays != null || fp.feedConsumedKg != null || fp.totalWeightGainKg != null);
                if (!hasData && feedRecords.length === 0) {
                  return (
                    <p className="text-sm text-avcs-muted">
                      No feed recorded for this deal yet. Use “Add feed” to capture a feed bill — kilograms count
                      towards FCR and the cost lands on the ledger. The occupancy fee above is the feedlot's own
                      standing charge and needs no invoice.
                    </p>
                  );
                }
                const tiles = [
                  { label: 'FCR', value: fp!.fcr != null ? `${Number(fp!.fcr).toFixed(2)}${fp!.fcrIsEstimated ? ' (est.)' : ''}` : '—' },
                  { label: 'Avg daily gain', value: fp!.avg_adg != null ? `${Number(fp!.avg_adg).toFixed(3)} kg` : '—' },
                  { label: 'Avg days in feedlot', value: fp!.avgFeedlotDays != null ? fmtNum(fp!.avgFeedlotDays) : '—' },
                  { label: 'Feed consumed', value: fp!.feedConsumedKg != null ? `${fmtNum(fp!.feedConsumedKg)} kg` : '—' },
                  { label: 'Total weight gain', value: fp!.totalWeightGainKg != null ? `${fmtNum(fp!.totalWeightGainKg)} kg` : '—' },
                ];
                return (
                  <>
                    {hasData && (
                      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
                        {tiles.map((t) => (
                          <div key={t.label} className="tile rounded-xl border border-[#1e6bb829] bg-white p-4">
                            <div className="tile-label">{t.label}</div>
                            <div className="tile-value">{t.value}</div>
                          </div>
                        ))}
                      </div>
                    )}
                    {feedRecords.length > 0 && (
                      <div className={hasData ? 'mt-4' : ''}>
                        <div className="mb-2 text-sm font-semibold text-avcs-text">Feed records ({feedRecords.length})</div>
                        <div className="grid gap-2">
                          {feedRecords.map((r) => (
                            <div key={r.id} className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-[#1e6bb824] bg-white px-3 py-2">
                              <div className="min-w-0">
                                <div className="text-sm font-semibold text-avcs-text">{r.description || r.documentTypeName || 'Feed record'}</div>
                                <div className="text-xs text-avcs-muted">
                                  {[r.entityName, r.periodStart && r.periodEnd ? `${fmtDate(r.periodStart)} – ${fmtDate(r.periodEnd)}` : `invoice ${fmtDate(r.invoiceDate)}`, r.quantityKg != null ? `${fmtNum(r.quantityKg)} kg` : '', r.sourceDocumentName ? `linked: ${r.sourceDocumentName}` : ''].filter(Boolean).join(' · ')}
                                </div>
                              </div>
                              <div className="flex flex-wrap items-center gap-2">
                                <span className="text-sm font-semibold tabular-nums text-avcs-text">{fmtMoney(r.cost)}</span>
                                {r.evidenceUrl && <a className="btn" href={normalizeDocumentUrl(r.evidenceUrl)} target="_blank" rel="noopener noreferrer">View document</a>}
                                <button type="button" className="rounded-md border border-[#e7bbb9] bg-white px-2 py-1 text-xs font-semibold text-[#6b1010] hover:bg-[#fdf1ef]" onClick={async () => {
                                  const ok = await confirm({
                                    title: 'Delete this feed record?',
                                    message: 'Its cost is removed from the deal\u2019s ledger and from its FCR.',
                                    confirmLabel: 'Delete record',
                                    tone: 'danger',
                                  });
                                  if (!ok) return;
                                  try { await deleteDealFeedRecord(r.id); loadFeedRecords(); loadFinances(); fetchTracking(selectedDealId).then(setTracking).catch(() => {}); } catch { /* shown on next load */ }
                                }}>Delete</button>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    <FeedAllocationPanel dealId={selectedDealId} isCompleted={isCompleted} refreshKey={`${feedRecords.length}:${finances.length}`} />
                  </>
                );
              })()}
            </Section>
            )}

            {retagTarget && (
              <RetagDialog
                animalId={retagTarget.id}
                currentEid={retagTarget.eid}
                onCancel={() => setRetagTarget(null)}
                onDone={() => { setRetagTarget(null); setDealAnimals([]); setRetagRefresh((n) => n + 1); }}
              />
            )}
            {tab === 'animals' && (
            <Section
              title="Animals"
              subtitle="Every animal on this deal."
              actions={<button className="btn" disabled={!selectedDealId} onClick={() => selectedDealId && onViewAnimals(selectedDealId)}>Open full list ↗</button>}
            >
              <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                <span className="text-sm text-avcs-muted">
                  {dealAnimalsLoading && dealAnimals.length === 0
                    ? 'Loading animals…'
                    : `${fmtNum(dealAnimalRows.length)}${dealAnimalsSearch.trim() ? ` of ${fmtNum(dealAnimals.length)}` : ''} animal${dealAnimalRows.length === 1 ? '' : 's'}`}
                </span>
              </div>

              <input
                className="mb-3 w-full rounded-lg border border-[#1e6bb824] bg-white px-3 py-2 text-sm"
                placeholder="Filter by EID, species, status…"
                value={dealAnimalsSearch}
                onChange={(e) => { setDealAnimalsSearch(e.target.value); setDealAnimalsPage(1); }}
              />

              {dealAnimalsError && (
                <div className="mb-3 rounded-lg border border-[#e7bbb9] bg-[#f8eae7] px-3 py-2 text-sm text-[#6b1010]">{dealAnimalsError}</div>
              )}

              {selectedDealId && (
                <RetagCandidates dealId={selectedDealId} onRetagged={() => setRetagRefresh((n) => n + 1)} />
              )}

              {(() => {
                const PAGE = 25;
                const totalPages = Math.max(1, Math.ceil(dealAnimalRows.length / PAGE));
                const page = Math.min(dealAnimalsPage, totalPages);
                const pageRows = dealAnimalRows.slice((page - 1) * PAGE, (page - 1) * PAGE + PAGE);
                return (
                  <>
                    <div className="admin-table-scroll tab-fill-scroll overflow-x-auto">
                      <table className="min-w-full border-collapse">
                        <thead>
                          <tr>
                            {['EID', 'Species', 'Status', 'Location', ''].map((h, i) => (
                              <th key={h || `actions-${i}`} className="border-b border-[#1e6bb824] px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-avcs-muted">{h}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {pageRows.map((r) => (
                            <tr key={r.animal_id} className="hover:bg-[#f8fbff]">
                              <td className="border-b border-[#1e6bb814] px-3 py-2 text-sm font-medium">
                                {onViewAnimal
                                  ? <button type="button" className="font-medium text-[#1e6bb8] hover:underline" onClick={() => onViewAnimal(r.animal_id, 'animals')}>{r.eid}</button>
                                  : r.eid}
                              </td>
                              <td className="border-b border-[#1e6bb814] px-3 py-2 text-sm capitalize text-avcs-text">{r.species ? r.species.toLowerCase() : '—'}</td>
                              <td className="border-b border-[#1e6bb814] px-3 py-2 text-sm text-avcs-text">{r.status || '—'}</td>
                              <td className="border-b border-[#1e6bb814] px-3 py-2 text-sm text-avcs-text">{r.location || '—'}</td>
                              <td className="border-b border-[#1e6bb814] px-3 py-2 text-right">
                                {r.eid && r.eid !== '—' && (
                                  <button
                                    type="button"
                                    className="rounded-md border border-[#1e6bb824] bg-white px-2 py-1 text-xs font-semibold text-avcs-text hover:bg-[#f1f7ff]"
                                    title="Replace this animal's EID, keeping its history"
                                    onClick={() => setRetagTarget({ id: r.animal_id, eid: r.eid })}
                                  >
                                    Re-tag
                                  </button>
                                )}
                              </td>
                            </tr>
                          ))}
                          {dealAnimalRows.length === 0 && (
                            <tr>
                              <td colSpan={5} className="px-3 py-3 text-sm text-avcs-muted">
                                {dealAnimalsLoading ? 'Loading animals…' : dealAnimals.length === 0 ? 'No animals recorded for this deal yet.' : 'No animals match your filter.'}
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                    {totalPages > 1 && (
                      <div className="mt-3 flex flex-wrap items-center justify-end gap-2 text-sm text-avcs-muted">
                        <button type="button" className="rounded-lg border border-[#1e6bb824] bg-white px-3 py-1.5 text-sm text-avcs-text disabled:opacity-40" disabled={page <= 1} onClick={() => setDealAnimalsPage((p) => Math.max(1, p - 1))}>Previous</button>
                        <span className="text-avcs-text">Page {page} of {totalPages}</span>
                        <button type="button" className="rounded-lg border border-[#1e6bb824] bg-white px-3 py-1.5 text-sm text-avcs-text disabled:opacity-40" disabled={page >= totalPages} onClick={() => setDealAnimalsPage((p) => Math.min(totalPages, p + 1))}>Next</button>
                      </div>
                    )}
                  </>
                );
              })()}

            </Section>
            )}

            {tab === 'slaughter' && (
            <Section
              title="Slaughter data"
              subtitle="Every row of the abattoir slaughter file(s) behind this deal, exactly as uploaded."
            >
              <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                <span className="text-sm text-avcs-muted">
                  {slaughterLoading && slaughterRawRows.length === 0
                    ? 'Loading slaughter data…'
                    : `${fmtNum(slaughterFiltered.length)}${slaughterSearch.trim() ? ` of ${fmtNum(slaughterRawRows.length)}` : ''} row${slaughterFiltered.length === 1 ? '' : 's'}`}
                </span>
                {slaughterUnlinkedCount > 0 && (
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="rounded-full border border-[#e8c9a6] bg-[#fdf3e7] px-2 py-0.5 text-xs font-semibold text-[#8a4b12]">
                      {slaughterUnlinkedCount} not on this deal
                    </span>
                    <button
                      type="button"
                      className="btn"
                      disabled={linkBusy !== '' || unlinkedAnimalIds.length === 0}
                      onClick={() => linkAnimals(unlinkedAnimalIds)}
                    >
                      {linkBusy === 'bulk' ? 'Linking…' : `Link all ${unlinkedAnimalIds.length} to this deal`}
                    </button>
                  </div>
                )}
              </div>

              {/* Revision history. Before this, a four-times-revised file was
                  indistinguishable from a first upload -- the tab rendered one
                  set of rows and said nothing about the others existing. */}
              {slaughterRevisedEvents.map((group) => {
                const head = group.revisions.find((r: any) => r.is_current);
                return (
                  <div key={group.event.id} className="mb-3 rounded-lg border border-[#1e6bb824] bg-[#f8fbff] px-3 py-2">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="rounded-full border border-[#b7dcc0] bg-[#eaf7ee] px-2 py-0.5 text-xs font-semibold text-[#1c6b32]">
                        Revision {group.current} of {group.count}
                      </span>
                      <span className="font-mono text-xs text-avcs-text">{head?.original_filename || '—'}</span>
                      {head?.uploaded_at && (
                        <span className="text-xs text-avcs-muted">uploaded {formatRevisionTimestamp(head.uploaded_at)}</span>
                      )}
                      <button
                        type="button"
                        className="ml-auto text-xs font-semibold text-[#1e6bb8] hover:underline"
                        aria-expanded={slaughterRevisionsOpen}
                        onClick={() => setSlaughterRevisionsOpen((v) => !v)}
                      >
                        {slaughterRevisionsOpen
                          ? 'Hide earlier revisions'
                          : `Show ${group.count - 1} earlier revision${group.count - 1 === 1 ? '' : 's'}`}
                      </button>
                    </div>
                    {slaughterRevisionsOpen && (
                      <ul className="mt-2 grid gap-1 border-t border-[#1e6bb814] pt-2">
                        {group.revisions.map((rev: any) => (
                          <li key={rev.data_session} className="flex flex-wrap items-center gap-2 text-xs">
                            <span className={rev.is_current
                              ? 'rounded border border-[#b7dcc0] bg-[#eaf7ee] px-1.5 py-0.5 font-semibold text-[#1c6b32]'
                              : 'rounded border border-[#1e6bb824] bg-white px-1.5 py-0.5 font-semibold text-avcs-muted'}
                            >
                              rev {rev.revision_number}{rev.is_current ? ' · current' : ''}
                            </span>
                            <span className="font-mono text-avcs-text">{rev.original_filename || '—'}</span>
                            {rev.uploaded_at && <span className="text-avcs-muted">{formatRevisionTimestamp(rev.uploaded_at)}</span>}
                            {rev.row_count != null && <span className="text-avcs-muted">{fmtNum(rev.row_count)} rows</span>}
                            {!rev.is_current && (
                              <span className="text-avcs-muted">
                                {slaughterDiffs[String(rev.data_session)]
                                  ?? (slaughterDiffsLoading ? 'comparing…' : '')}
                              </span>
                            )}
                            <button
                              type="button"
                              className="ml-auto font-semibold text-[#1e6bb8] hover:underline"
                              onClick={() => openEventRawData(group.event, String(rev.data_session))}
                            >
                              {rev.is_current ? 'View rows' : 'View rows (read only)'}
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                );
              })}

              <input
                className="mb-3 w-full rounded-lg border border-[#1e6bb824] bg-white px-3 py-2 text-sm"
                placeholder="Filter by EID, file, or any raw value…"
                value={slaughterSearch}
                onChange={(e) => setSlaughterSearch(e.target.value)}
              />

              {slaughterError && (
                <div className="mb-3 rounded-lg border border-[#e7bbb9] bg-[#f8eae7] px-3 py-2 text-sm text-[#6b1010]">{slaughterError}</div>
              )}
              {linkNotice && (
                <div className="mb-3 rounded-lg border border-[#b9e7c9] bg-[#e7f8ee] px-3 py-2 text-sm text-[#106b38]">{linkNotice}</div>
              )}
              {linkError && (
                <div className="mb-3 rounded-lg border border-[#e7bbb9] bg-[#f8eae7] px-3 py-2 text-sm text-[#6b1010]">
                  {linkError}
                  {pendingForceIds.length > 0 && (
                    <button
                      type="button"
                      className="btn ml-2"
                      disabled={linkBusy !== ''}
                      onClick={() => linkAnimals(pendingForceIds, true)}
                    >Move anyway</button>
                  )}
                </div>
              )}

              <div className="admin-table-scroll slaughter-raw-scroll tab-fill-scroll overflow-x-auto">
                <table className="min-w-full border-collapse">
                  <thead>
                    <tr>
                      {['Animal', 'On this deal', 'Row', 'File', ...slaughterColumns].map((h, i) => (
                        <th key={`${h}-${i}`} className="whitespace-nowrap border-b border-[#1e6bb824] px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-avcs-muted">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {slaughterFiltered.map((r) => {
                      // "On this deal" is the point of the column: a row can
                      // resolve to an animal that sits on another deal (or none
                      // at all), in which case it is absent from this deal's
                      // slaughter counts even though it is in the file.
                      const onDeal = Boolean(r.animal_deal_id && r.animal_deal_id === selectedDealId);
                      // A row with no EID fell back to the label "Linked
                      // animal", which names nothing. An untagged animal is
                      // still identified -- by the VID the abattoir's sequence
                      // number was recorded as -- so show that instead.
                      const rowVid = String((r.mapped_data || {}).vid || (r.raw_data || {}).vid || '');
                      const label = r.animal_eid
                        || String((r.mapped_data || {}).eid || '')
                        || (rowVid ? `VID ${rowVid}` : '')
                        || (r.animal_id ? 'Linked animal' : '');
                      // Why the identifier is blank, as recorded at ingest --
                      // the backend sends these only for a row with no EID.
                      const identifierNotes = (r.animal_identifier_notes || [])
                        .map((n) => n.note)
                        .filter(Boolean);
                      const identifierTitle = identifierNotes.join('\n\n');
                      // The sticky first column needs its own opaque
                      // background -- a transparent sticky cell lets the
                      // scrolled columns show through it -- so the row's
                      // background travels onto the cell rather than only
                      // onto the <tr>.
                      const rowBg = onDeal ? 'bg-white' : 'bg-[#fffaf3]';
                      return (
                        <tr key={r.id} className={onDeal ? 'hover:bg-[#f8fbff]' : 'bg-[#fffaf3] hover:bg-[#fdf3e7]'}>
                          <td className={`sticky left-0 z-[1] whitespace-nowrap border-b border-[#1e6bb814] px-3 py-2 text-sm font-medium ${rowBg}`}>
                            {r.animal_id ? (
                              onViewAnimal ? (
                                <button type="button" className="font-mono text-[#1e6bb8] hover:underline" onClick={() => onViewAnimal(r.animal_id!)}>
                                  {label || 'View animal'}
                                </button>
                              ) : <span className="font-mono">{label || '—'}</span>
                            ) : (
                              <span className="text-avcs-muted">No animal record</span>
                            )}
                            {/* An untagged animal is not a gap in the file.
                                Show what was recorded about identifying it,
                                rather than leaving the blank to read as
                                missing data. */}
                            {identifierNotes.length > 0 && (
                              <span
                                className="ml-1 cursor-help rounded-full border border-[#1e6bb833] bg-[#f1f7ff] px-1.5 py-0.5 text-[10px] font-semibold text-[#154f89]"
                                title={identifierTitle}
                                tabIndex={0}
                                role="note"
                                aria-label={`Why this animal has no tag: ${identifierTitle}`}
                              >
                                no tag · why?
                              </span>
                            )}
                            {r.animal_deleted && (
                              <span className="ml-1 text-[11px] font-semibold text-[#6b1010]" title="This animal has been deleted and is excluded from every count">deleted</span>
                            )}
                          </td>
                          <td className="whitespace-nowrap border-b border-[#1e6bb814] px-3 py-2 text-sm">
                            {/* Tick/cross as well as colour — never colour alone. */}
                            {onDeal ? (
                              <span className="inline-flex items-center gap-1 rounded-full border border-[#b7dcc0] bg-[#eaf7ee] px-2 py-0.5 text-[11px] font-semibold text-[#1c6b32]">
                                <span aria-hidden="true">✓</span> Yes
                              </span>
                            ) : (
                              <span className="inline-flex flex-wrap items-center gap-1">
                                <span className="inline-flex items-center gap-1 rounded-full border border-[#e8c9a6] bg-[#fdf3e7] px-2 py-0.5 text-[11px] font-semibold text-[#8a4b12]">
                                  <span aria-hidden="true">✕</span> No
                                </span>
                                {/* Only offer the link where there is an animal
                                    to link. A row that resolved to nothing needs
                                    EID reconciliation first, not a deal link. */}
                                {r.animal_id && !r.animal_deleted && (
                                  <button
                                    type="button"
                                    className="rounded-lg border border-[#1e6bb833] bg-white px-2 py-0.5 text-[11px] font-semibold text-[#1e6bb8] hover:bg-[#f8fbff] disabled:opacity-40"
                                    disabled={linkBusy !== ''}
                                    onClick={() => linkAnimals([r.animal_id!])}
                                  >
                                    {linkBusy === r.animal_id ? 'Linking…' : 'Link to deal'}
                                  </button>
                                )}
                              </span>
                            )}
                          </td>
                          <td className="whitespace-nowrap border-b border-[#1e6bb814] px-3 py-2 text-sm tabular-nums text-avcs-muted">{r.row_number ?? '—'}</td>
                          <td className="whitespace-nowrap border-b border-[#1e6bb814] px-3 py-2 text-xs text-avcs-muted" title={r.original_filename || ''}>{r.original_filename || '—'}</td>
                          {slaughterColumns.map((col) => {
                            const val = r.raw_data ? (r.raw_data as Record<string, unknown>)[col] : undefined;
                            const text = val == null || val === '' ? '—' : String(val);
                            // Abattoir `notes` carries a paragraph (product
                            // code, price basis, hook number, class), which as
                            // a nowrap column pushes every column after it off
                            // screen. Truncate anything long and let the cell
                            // expand in place instead of widening the table.
                            const isLong = text.length > SLAUGHTER_CELL_MAX;
                            // An empty identifier column on a row we have an
                            // explanation for: hovering says why rather than
                            // leaving a bare dash that reads as lost data.
                            const isBlankIdentifier = text === '—'
                              && identifierNotes.length > 0
                              && IDENTIFIER_COLUMNS.has(col.toLowerCase());
                            if (isBlankIdentifier) {
                              return (
                                <td key={col} className="whitespace-nowrap border-b border-[#1e6bb814] px-3 py-2 text-sm">
                                  <span className="cursor-help text-avcs-muted underline decoration-dotted underline-offset-2" title={identifierTitle} tabIndex={0}>
                                    none
                                  </span>
                                </td>
                              );
                            }
                            if (!isLong) {
                              return <td key={col} className="whitespace-nowrap border-b border-[#1e6bb814] px-3 py-2 text-sm text-avcs-text" title={text}>{text}</td>;
                            }
                            const key = `${r.id}:${col}`;
                            const open = slaughterExpanded.has(key);
                            return (
                              <td key={col} className="border-b border-[#1e6bb814] px-3 py-2 text-sm text-avcs-text">
                                <button
                                  type="button"
                                  className={`max-w-[22rem] cursor-pointer text-left align-top hover:underline ${open ? 'whitespace-pre-wrap' : 'block truncate whitespace-nowrap'}`}
                                  title={open ? 'Collapse' : text}
                                  aria-expanded={open}
                                  onClick={() => setSlaughterExpanded((prev) => {
                                    const next = new Set(prev);
                                    if (next.has(key)) next.delete(key); else next.add(key);
                                    return next;
                                  })}
                                >
                                  {open ? text : `${text.slice(0, SLAUGHTER_CELL_MAX)}…`}
                                </button>
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })}
                    {slaughterFiltered.length === 0 && (
                      <tr>
                        <td colSpan={slaughterColumns.length + 4} className="px-3 py-3 text-sm text-avcs-muted">
                          {slaughterLoading
                            ? 'Loading slaughter data…'
                            : slaughterRawRows.length === 0
                              ? 'No abattoir-slaughter file has been uploaded for this deal yet.'
                              : 'No rows match your filter.'}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </Section>
            )}
          </>
        )}
      </div>

      {/* Audit trail → "View raw data": every row of the file an event was built from. */}
      {rawData && (
        <RawDataModal
          state={rawData}
          onClose={() => { setRawData(null); setRawDataEvent(null); }}
          onReload={() => rawDataEvent && openEventRawData(rawDataEvent)}
        />
      )}
    </section>
  );
}

// =============================================================================
// Financial
// =============================================================================

// The deal's money ledger -- every DealFinance row -- plus a form to add one.
// Sits under the derived cost report: the report is what the system computed,
// the ledger is what was actually booked, and both belong on one tab.
function DealFinanceLedger({
  dealId,
  finances,
  onChanged,
}: {
  dealId: string;
  finances: DealFinanceRow[];
  onChanged: () => void;
}) {
  const confirm = useConfirm();
  const [adding, setAdding] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState<{ recordType: CreateDealFinanceInput['recordType']; amount: string; transactionDate: string; description: string; referenceNumber: string }>({
    recordType: 'OTHER_COST',
    amount: '',
    transactionDate: new Date().toISOString().slice(0, 10),
    description: '',
    referenceNumber: '',
  });
  const typeMeta = DEAL_FINANCE_TYPES.find((t) => t.value === form.recordType);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!dealId) return;
    if (!form.amount.trim() || Number(form.amount) <= 0) { setError('Enter the amount as a positive total — the type decides whether it is a cost or revenue.'); return; }
    if (!form.description.trim()) { setError('A description is required.'); return; }
    setSaving(true);
    setError('');
    try {
      await createDealFinance({
        dealId,
        recordType: form.recordType,
        amount: form.amount.trim(),
        transactionDate: form.transactionDate,
        description: form.description.trim(),
        referenceNumber: form.referenceNumber.trim() || undefined,
      });
      setForm((prev) => ({ ...prev, amount: '', description: '', referenceNumber: '' }));
      setAdding(false);
      onChanged();
    } catch (err: any) {
      setError(err?.message || 'Could not save the finance record.');
    } finally {
      setSaving(false);
    }
  };

  const net = finances.reduce((acc, r) => acc + (Number.isFinite(r.amount) ? r.amount : 0), 0);

  return (
    <Section
      title="Finance records"
      subtitle="Every amount booked against this deal. Costs are shown negative, revenue positive; the sign follows the record type."
      actions={
        <button type="button" className="btn primary" onClick={() => { setAdding((v) => !v); setError(''); }}>
          {adding ? 'Close' : 'New finance record'}
        </button>
      }
    >
      {adding && (
        <form onSubmit={submit} className="mb-3 rounded-xl border border-[#1e6bb824] bg-white p-3">
          {error && <div className="mb-3 rounded-lg border border-[#e7bbb9] bg-[#f8eae7] px-3 py-2 text-sm text-[#6b1010]">{error}</div>}
          <div className="grid gap-3 md:grid-cols-2">
            <label className="grid gap-1 text-sm text-avcs-text">
              Type
              <select
                className="rounded-lg border border-[#1e6bb833] px-3 py-2"
                value={form.recordType}
                onChange={(e) => setForm((prev) => ({ ...prev, recordType: e.target.value as CreateDealFinanceInput['recordType'] }))}
              >
                {DEAL_FINANCE_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
              <span className="text-xs text-avcs-muted">{typeMeta?.isCost ? 'Booked as a cost.' : 'Booked as revenue.'}</span>
            </label>
            <label className="grid gap-1 text-sm text-avcs-text">
              Amount (R, ex VAT)
              <input
                type="number"
                min="0.01"
                step="0.01"
                className="rounded-lg border border-[#1e6bb833] px-3 py-2"
                value={form.amount}
                onChange={(e) => setForm((prev) => ({ ...prev, amount: e.target.value }))}
                required
              />
            </label>
            <label className="grid gap-1 text-sm text-avcs-text">
              Date
              <input
                type="date"
                className="rounded-lg border border-[#1e6bb833] px-3 py-2"
                value={form.transactionDate}
                onChange={(e) => setForm((prev) => ({ ...prev, transactionDate: e.target.value }))}
                required
              />
            </label>
            <label className="grid gap-1 text-sm text-avcs-text">
              Reference <span className="text-avcs-muted">(invoice / document no, optional)</span>
              <input
                className="rounded-lg border border-[#1e6bb833] px-3 py-2"
                value={form.referenceNumber}
                onChange={(e) => setForm((prev) => ({ ...prev, referenceNumber: e.target.value }))}
              />
            </label>
            <label className="grid gap-1 text-sm text-avcs-text md:col-span-2">
              Description
              <input
                className="rounded-lg border border-[#1e6bb833] px-3 py-2"
                value={form.description}
                onChange={(e) => setForm((prev) => ({ ...prev, description: e.target.value }))}
                placeholder="e.g. Kyto feed invoice 3000665, June"
                required
              />
            </label>
          </div>
          <div className="mt-3 flex gap-2">
            <button type="submit" className="btn primary" disabled={saving}>{saving ? 'Saving…' : 'Save record'}</button>
            <button type="button" className="btn" disabled={saving} onClick={() => setAdding(false)}>Cancel</button>
          </div>
        </form>
      )}

      {finances.length === 0 ? (
        <p className="text-sm text-avcs-muted">Nothing booked against this deal yet.</p>
      ) : (
        <div className="admin-table-scroll overflow-x-auto">
          <table className="min-w-full border-collapse">
            <thead>
              <tr>
                {['Date', 'Type', 'Description', 'Reference', 'Document', 'Amount', ''].map((h) => (
                  <th key={h} className={`whitespace-nowrap border-b border-[#1e6bb824] px-3 py-2 text-xs font-semibold uppercase tracking-wide text-avcs-muted ${h === 'Amount' ? 'text-right' : 'text-left'}`}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {finances.map((row) => (
                <tr key={row.id}>
                  <td className="whitespace-nowrap border-b border-[#1e6bb814] px-3 py-2 text-sm text-avcs-text">{fmtDate(row.transactionDate)}</td>
                  <td className="whitespace-nowrap border-b border-[#1e6bb814] px-3 py-2 text-sm text-avcs-text">
                    {row.recordTypeLabel}
                    {row.linkedToDocument && <span className="ml-1.5 text-xs text-avcs-muted" title="Mirrors captured evidence (a document, wool sale, medication, shared feedlot cost or transport journey) — edit that, not this row.">· linked</span>}
                  </td>
                  <td className="border-b border-[#1e6bb814] px-3 py-2 text-sm text-avcs-text">{row.description || '—'}</td>
                  <td className="whitespace-nowrap border-b border-[#1e6bb814] px-3 py-2 text-sm text-avcs-muted">{row.referenceNumber || '—'}</td>
                  <td className="whitespace-nowrap border-b border-[#1e6bb814] px-3 py-2 text-sm">
                    {row.documentUrl ? (
                      <a className="btn admin-inline-action" href={normalizeDocumentUrl(row.documentUrl)} target="_blank" rel="noopener noreferrer" title={row.documentName || 'Open the document behind this row'}>
                        View{row.documentName ? ` ${row.documentName.toLowerCase()}` : ' document'}
                      </a>
                    ) : (
                      <span className="text-avcs-muted">—</span>
                    )}
                  </td>
                  <td className={`whitespace-nowrap border-b border-[#1e6bb814] px-3 py-2 text-right text-sm tabular-nums ${row.amount < 0 ? 'text-[#6b1010]' : 'text-[#106b38]'}`}>{fmtMoney(row.amount)}</td>
                  <td className="whitespace-nowrap border-b border-[#1e6bb814] px-3 py-2 text-right text-sm">
                    {row.linkedToDocument ? (
                      <span className="text-xs text-avcs-muted" title="Remove the document, journey, feed record or shared cost it mirrors and this row goes with it.">via source</span>
                    ) : (
                      <button
                        type="button"
                        className="rounded-md border border-[#e7bbb9] bg-white px-2 py-1 text-xs font-semibold text-[#6b1010] hover:bg-[#fdf1ef]"
                        onClick={async () => {
                          const ok = await confirm({
                            title: `Delete this ${row.recordTypeLabel.toLowerCase()} row?`,
                            message: `${fmtMoney(Math.abs(row.amount))}. It is removed from the deal\u2019s ledger and its cost figures.`,
                            confirmLabel: 'Delete row',
                            tone: 'danger',
                          });
                          if (!ok) return;
                          try {
                            await deleteDealFinance(row.id);
                            onChanged();
                          } catch (e: any) {
                            setError(e?.message || 'Could not delete the row.');
                          }
                        }}
                      >
                        Delete
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan={5} className="px-3 py-2 text-right text-sm font-semibold text-avcs-text">Net</td>
                <td className={`px-3 py-2 text-right text-sm font-semibold tabular-nums ${net < 0 ? 'text-[#6b1010]' : 'text-[#106b38]'}`}>{fmtMoney(net)}</td>
                <td />
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </Section>
  );
}

function FinancialSection({
  report,
  dealId,
  finances,
  onChanged,
}: {
  report: DealAdminReport | null;
  dealId: string;
  finances: DealFinanceRow[];
  onChanged: () => void;
}) {
  const ledger = <DealFinanceLedger dealId={dealId} finances={finances} onChanged={onChanged} />;
  if (!report || !report.costs) {
    return (
      <>
        <Section title="Financials" subtitle="Cost of moving these animals through the whole supply chain program.">
          <p className="text-sm text-avcs-muted">Cost report unavailable for this deal.</p>
        </Section>
        {ledger}
      </>
    );
  }
  const sources = report.costs?.sources || {};
  const groups = COST_GROUPS.map((group) => {
    const rows = group.keys
      .map((key) => ({ key, label: COST_KEY_LABELS[key] || key, value: Number(sources[key] || 0) }))
      .filter((row) => Math.abs(row.value) > 0);
    const subtotal = rows.reduce((acc, r) => acc + r.value, 0);
    return { ...group, rows, subtotal };
  }).filter((group) => group.rows.length > 0);

  return (
    <>
    <Section title="Financials" subtitle="Cost through the supply chain program, revenue from sale value, and the resulting profit / loss.">
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <div className="tile rounded-xl border border-[#1e6bb829] bg-white p-4">
          <div className="tile-label">Total cost</div>
          <div className="tile-value">{fmtMoney(report.costs?.total_cost)}</div>
          <div className="mt-1 text-xs text-avcs-muted">{fmtMoney(report.costs?.per_animal_cost)} / animal</div>
        </div>
        <div className="tile rounded-xl border border-[#1e6bb829] bg-white p-4">
          <div className="tile-label">Revenue (sale value)</div>
          <div className="tile-value">{fmtMoney(report.costs?.revenue)}</div>
        </div>
        {(() => {
          const pl = report.costs?.profit_loss;
          const has = pl !== null && pl !== undefined;
          const isLoss = has && Number(pl) < 0;
          return (
            <div className={`tile rounded-xl border p-4 ${has ? (isLoss ? 'border-[#e7bbb9] bg-[#fdf1ef]' : 'border-[#b9e7c9] bg-[#f0faf4]') : 'border-[#1e6bb829] bg-white'}`}>
              <div className="tile-label">{isLoss ? 'Loss' : 'Profit / loss'}</div>
              <div className={`tile-value ${has ? (isLoss ? 'text-[#6b1010]' : 'text-[#106b38]') : ''}`}>{fmtMoney(pl)}</div>
              <div className="mt-1 text-xs text-avcs-muted">{fmtMoney(report.costs?.per_animal_profit)} / animal</div>
            </div>
          );
        })()}
        <div className="tile rounded-xl border border-[#1e6bb829] bg-white p-4">
          <div className="tile-label">Animals costed</div>
          <div className="tile-value">{fmtNum(report.costs?.animal_count)}</div>
        </div>
      </div>

      <WoolSummary dealId={dealId} refreshKey={finances.length} />

      {groups.length === 0 ? (
        <p className="mt-3 text-sm text-avcs-muted">No itemised costs captured for this deal yet.</p>
      ) : (
        <div className="mt-3 grid gap-3 lg:grid-cols-2">
          {groups.map((group) => (
            <div key={group.label} className="rounded-xl border border-[#1e6bb824] bg-white p-3">
              <div className="mb-2 flex items-center justify-between gap-2">
                <div className="text-sm font-semibold text-avcs-text">{group.label}</div>
                <div className="text-sm font-semibold text-avcs-primary">{fmtMoney(group.subtotal)}</div>
              </div>
              <div className="grid gap-1">
                {group.rows.map((row) => (
                  <div key={row.key} className="flex items-center justify-between text-sm">
                    <span className="text-avcs-muted">{row.label}</span>
                    <span className="tabular-nums text-avcs-text">{fmtMoney(row.value)}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </Section>
    {ledger}
    </>
  );
}

// =============================================================================
// Health / vaccination
// =============================================================================

/** Full product name: the medicine's own name, plus its brand and producer when
 *  they add something. Abbreviating these makes two similar products on one
 *  deal indistinguishable. */
function medicineName(v: any): string {
  const m = v?.medicine || {};
  const parts = [m.name, m.brand_name, m.producer]
    .map((part: any) => (part ? String(part).trim() : ''))
    .filter(Boolean);
  const unique = parts.filter((part, i) => parts.findIndex((p) => p.toLowerCase() === part.toLowerCase()) === i);
  if (unique.length > 0) return unique.join(' · ');
  return v?.vaccination_type_display || v?.vaccination_type || 'Treatment';
}

// Cost lines off a document that is already filed on the deal: each line with
// events becomes a shared record pointing at that document (no second upload);
// a line with no events is divided across the deal's animals as a ledger row.
function DocumentCostLinesPanel({ dealId, dealNo, headcount, documentId, onDone, onCancel }: {
  dealId: string; dealNo: string; headcount: number; documentId: string; onDone: () => void; onCancel: () => void;
}) {
  const [doc, setDoc] = useState<DealDocumentSummary | null>(null);
  const [recordType, setRecordType] = useState('OTHER_COST');
  const [lines, setLines] = useState<CostLine[]>([newCostLine()]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  useEffect(() => {
    fetchDealDocumentsForDeal(dealId).then((rows) => {
      const found = rows.find((r) => r.id === documentId) || null;
      setDoc(found);
      const kind = found?.financeRecordType || '';
      const meta = DEAL_FINANCE_TYPES.find((t) => t.value === kind);
      if (meta?.isCost) setRecordType(kind);
      setLines([newCostLine(kind === 'TRANSPORT' ? 'MOVEMENT' : kind === 'SHEARING' ? 'SHEARING' : kind === 'VETERINARY' ? 'VACCINATION' : 'ALL')]);
    }).catch(() => setDoc(null));
  }, [dealId, documentId]);
  const save = async () => {
    if (lines.length === 0 || lines.some((l) => !costLineIsComplete(l))) { setError('Every cost line needs a description and a cost — or remove the empty line.'); return; }
    setSaving(true); setError('');
    try {
      for (const line of lines) {
        if (line.eventIds.length > 0) {
          await createSharedFinance({
            recordType, amount: line.amount, invoiceDate: doc?.dateOfDocument || new Date().toISOString().slice(0, 10),
            basis: 'EVENT_ANIMALS', basisEventIds: line.eventIds, sourceDocumentId: documentId,
            description: line.description.trim(), referenceNumber: doc?.referenceNumber || undefined, document: null,
          });
        } else {
          for (const share of splitByHead(Number(line.amount), [{ key: dealId, animals: headcount }])) {
            if (share.amount <= 0) continue;
            await createDealFinance({ dealId, recordType: recordType as CreateDealFinanceInput['recordType'], amount: share.amount.toFixed(2), transactionDate: doc?.dateOfDocument || new Date().toISOString().slice(0, 10), description: `${line.description.trim()} — ${share.animals} head` });
          }
        }
      }
      onDone();
    } catch (e: any) {
      setError(e?.message || 'Could not save the cost lines.');
    } finally {
      setSaving(false);
    }
  };
  const costTypes = DEAL_FINANCE_TYPES.filter((t) => t.isCost);
  return (
    <div className="mt-3 rounded-xl border border-[#c8e6d4a0] bg-[#f7fdf9] p-4">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <p className="text-base font-semibold text-[#1a5c38]">Cost lines — {doc ? [doc.documentTypeName, doc.referenceNumber ? `Ref ${doc.referenceNumber}` : ''].filter(Boolean).join(' · ') : 'document'}</p>
          <p className="mt-0.5 text-xs text-avcs-muted">Each record links this filed document, so nothing is uploaded twice. A line with events is split equally across the animals on them; a line with no events is divided across the deal's {headcount} animals.</p>
        </div>
        <label className="grid gap-1 text-sm text-avcs-text">
          Cost type
          <select className="rounded-lg border border-[#1e6bb833] bg-white px-3 py-2" value={recordType} onChange={(e) => setRecordType(e.target.value)}>
            {costTypes.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
        </label>
      </div>
      {error && <div className="mt-2 rounded-lg border border-[#e7bbb9] bg-[#f8eae7] px-3 py-2 text-sm text-[#6b1010]">{error}</div>}
      <div className="mt-3">
        <CostLineItems dealHeads={[{ dealId, dealNo, animals: headcount }]} lines={lines} onChange={setLines} />
      </div>
      <div className="mt-3 flex flex-wrap gap-2">
        <button type="button" className="btn primary" disabled={saving} onClick={save}>{saving ? 'Saving…' : `Book ${lines.length} line${lines.length === 1 ? '' : 's'} (R ${costLinesTotal(lines).toFixed(2)})`}</button>
        <button type="button" className="btn" disabled={saving} onClick={onCancel}>Cancel</button>
      </div>
    </div>
  );
}

function HealthSection({
  vaccinations,
  dealId,
  dealEvents,
  onViewRawData,
  onInvoiceSaved,
}: {
  vaccinations: any[];
  dealId?: string;
  /** The deal's events, used to resolve each record's source upload. */
  dealEvents?: any[];
  onViewRawData?: (evt: any) => void;
  /** After an invoice is costed onto the treated animals: re-read what it changed. */
  onInvoiceSaved?: () => void;
}) {
  // "Add invoice": cost one upload's medicines from the invoice. The endpoint
  // works per ingest session (the file that carried the medicines), so the
  // records are grouped by the session behind their event; a group with a
  // cost already is shown as such and can be re-costed.
  const [invoiceOpen, setInvoiceOpen] = useState(false);
  const [invoiceSessionId, setInvoiceSessionId] = useState('');
  const [invoiceForm, setInvoiceForm] = useState<{ costPerAnimal: string; referenceNumber: string; invoiceDate: string; file: File | null }>({ costPerAnimal: '', referenceNumber: '', invoiceDate: '', file: null });
  const [invoiceSaving, setInvoiceSaving] = useState(false);
  const [invoiceError, setInvoiceError] = useState('');
  const [invoiceSuccess, setInvoiceSuccess] = useState('');
  // Each DealVaccination is built from an ingest event, and that event carries
  // the file it came from — so the source is resolvable without a new endpoint.
  const eventById = useMemo(() => {
    const map: Record<string, any> = {};
    (Array.isArray(dealEvents) ? dealEvents : []).forEach((e: any) => {
      if (e?.id) map[String(e.id)] = e;
    });
    return map;
  }, [dealEvents]);
  // Which vaccination's animal list is open, and its contents.
  const [openVax, setOpenVax] = useState<any | null>(null);
  const invoiceGroups = useMemo(() => {
    const groups = new Map<string, { sessionId: string; date: string; medicines: string[]; animals: number; costed: boolean }>();
    (vaccinations || []).forEach((v: any) => {
      const evt = v?.deal_event ? eventById[String(v.deal_event)] : null;
      const sessionId = evt?.data_session ? String(evt.data_session) : '';
      if (!sessionId) return;
      const group = groups.get(sessionId) || { sessionId, date: String(v?.date_given || ''), medicines: [], animals: 0, costed: false };
      const name = String(v?.medicine?.name || v?.vaccination_type_display || v?.vaccination_type || '').trim();
      if (name && !group.medicines.includes(name)) group.medicines.push(name);
      group.animals = Math.max(group.animals, Number(v?.animals_vaccinated || 0));
      if (Number(v?.total_cost || 0) > 0) group.costed = true;
      groups.set(sessionId, group);
    });
    return Array.from(groups.values()).sort((a, b) => b.date.localeCompare(a.date));
  }, [vaccinations, eventById]);
  const selectedInvoiceGroup = invoiceGroups.find((g) => g.sessionId === invoiceSessionId) || null;
  const openInvoice = () => {
    setInvoiceOpen(true);
    setInvoiceError('');
    setInvoiceSuccess('');
    if (!invoiceSessionId) {
      const first = invoiceGroups.find((g) => !g.costed) || invoiceGroups[0];
      setInvoiceSessionId(first?.sessionId || '');
      setInvoiceForm((prev) => ({ ...prev, invoiceDate: prev.invoiceDate || (first?.date || '').slice(0, 10) }));
    }
  };
  const saveInvoice = async () => {
    if (!invoiceSessionId || invoiceForm.costPerAnimal === '' || Number(invoiceForm.costPerAnimal) < 0) return;
    setInvoiceSaving(true);
    setInvoiceError('');
    try {
      const result = await submitTreatmentInvoice(invoiceSessionId, {
        costPerAnimal: invoiceForm.costPerAnimal,
        referenceNumber: invoiceForm.referenceNumber.trim() || undefined,
        invoiceDate: invoiceForm.invoiceDate || undefined,
        file: invoiceForm.file,
      });
      setInvoiceSuccess(
        `R ${result.total} booked as a veterinary cost across ${fmtNum(result.animals)} animals (${result.medicines.length} medicine${result.medicines.length === 1 ? '' : 's'})`
        + (result.document_id ? ', invoice filed.' : result.document_skipped ? '. The invoice was not filed: seed the INVOICE document type first.' : '.'),
      );
      setInvoiceForm({ costPerAnimal: '', referenceNumber: '', invoiceDate: '', file: null });
      setInvoiceOpen(false);
      onInvoiceSaved?.();
    } catch (e: any) {
      setInvoiceError(e?.message || 'Could not save the invoice.');
    } finally {
      setInvoiceSaving(false);
    }
  };
  const [vaxAnimals, setVaxAnimals] = useState<DealEventAnimal[]>([]);
  const [vaxLoading, setVaxLoading] = useState(false);
  const [vaxError, setVaxError] = useState('');

  const openAnimals = async (v: any) => {
    setOpenVax(v);
    setVaxAnimals([]);
    setVaxError('');
    if (!dealId || !v?.deal_event) {
      // No event was recorded for this administration, so there is no animal
      // list to show — say so rather than spinning forever.
      setVaxError('This record has no linked event, so its animals cannot be listed.');
      return;
    }
    setVaxLoading(true);
    try {
      setVaxAnimals(await fetchDealEventAnimals(dealId, String(v.deal_event)));
    } catch (e: any) {
      setVaxError(e?.message || 'Could not load the animals for this record.');
    } finally {
      setVaxLoading(false);
    }
  };

  return (
    <Section
      title="Health & vaccinations"
      subtitle="Medical treatments and vaccinations recorded against this deal."
      actions={invoiceGroups.length > 0 ? (
        <button type="button" className="btn primary" onClick={openInvoice} title="Cost an upload's medicines from the invoice and apply it to the treated animals">
          Add invoice
        </button>
      ) : undefined}
    >
      {invoiceSuccess && <div className="mb-3 rounded-lg border border-[#b9e7c9] bg-[#e7f8ee] px-3 py-2 text-sm text-[#106b38]">{invoiceSuccess}</div>}
      {invoiceOpen && (
        <div className="mb-4 rounded-xl border border-[#c8e6d4a0] bg-[#f7fdf9] p-4 min-w-0">
          <div className="flex flex-wrap items-start justify-between gap-2">
            <div>
              <p className="text-base font-semibold text-[#1a5c38]">Invoice &amp; cost</p>
              <p className="mt-0.5 text-xs text-avcs-muted">
                The cost per animal is split evenly across the medicines on the upload, recorded on every treated animal, summed per medicine and booked to the deal as a veterinary cost. The PDF is filed against the deal.
              </p>
            </div>
            <button type="button" className="btn" onClick={() => setInvoiceOpen(false)}>Close</button>
          </div>
          {invoiceError && <div className="mt-2 rounded-lg border border-[#e7bbb9] bg-[#f8eae7] px-3 py-2 text-sm text-[#6b1010]">{invoiceError}</div>}
          <div className="mt-3 grid gap-3 md:grid-cols-2">
            <label className="grid gap-1 text-sm text-avcs-text md:col-span-2">
              Treatment upload
              <select className="rounded-lg border border-[#1e6bb833] bg-white px-3 py-2" value={invoiceSessionId} onChange={(e) => { setInvoiceSessionId(e.target.value); const g = invoiceGroups.find((x) => x.sessionId === e.target.value); if (g && !invoiceForm.invoiceDate) setInvoiceForm((prev) => ({ ...prev, invoiceDate: g.date.slice(0, 10) })); }}>
                {invoiceGroups.map((g) => (
                  <option key={g.sessionId} value={g.sessionId}>
                    {fmtDate(g.date)} · {g.medicines.length} medicine{g.medicines.length === 1 ? '' : 's'} · {fmtNum(g.animals)} animals{g.costed ? ' · costed — saving re-costs it' : ''}
                  </option>
                ))}
              </select>
              {selectedInvoiceGroup && <span className="text-xs text-avcs-muted">{selectedInvoiceGroup.medicines.join(' · ')}</span>}
            </label>
            <label className="grid gap-1 text-sm text-avcs-text">
              Cost per animal (R, ex VAT) *
              <input type="number" min={0} step="0.01" className="rounded-lg border border-[#1e6bb833] px-3 py-2" value={invoiceForm.costPerAnimal} onChange={(e) => setInvoiceForm((prev) => ({ ...prev, costPerAnimal: e.target.value }))} />
              {selectedInvoiceGroup && invoiceForm.costPerAnimal !== '' && Number(invoiceForm.costPerAnimal) >= 0 && (
                <span className="text-xs text-avcs-muted">Deal cost: R {(Number(invoiceForm.costPerAnimal) * selectedInvoiceGroup.animals).toFixed(2)}</span>
              )}
            </label>
            <div className="grid gap-3 sm:grid-cols-2">
              <label className="grid gap-1 text-sm text-avcs-text">
                Invoice number
                <input className="rounded-lg border border-[#1e6bb833] px-3 py-2" value={invoiceForm.referenceNumber} onChange={(e) => setInvoiceForm((prev) => ({ ...prev, referenceNumber: e.target.value }))} />
              </label>
              <label className="grid gap-1 text-sm text-avcs-text">
                Invoice date
                <input type="date" className="rounded-lg border border-[#1e6bb833] px-3 py-2" value={invoiceForm.invoiceDate} onChange={(e) => setInvoiceForm((prev) => ({ ...prev, invoiceDate: e.target.value }))} />
              </label>
            </div>
            <div className="grid gap-1 text-sm text-avcs-text md:col-span-2">
              Invoice (PDF) <span className="text-avcs-muted">(optional — filed against the deal)</span>
              <FileDropzone file={invoiceForm.file} onFileChange={(file: File | null) => setInvoiceForm((prev) => ({ ...prev, file }))} acceptedExtensions={['pdf', 'jpg', 'jpeg', 'png']} />
            </div>
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            <button type="button" className="btn primary" disabled={invoiceSaving || !invoiceSessionId || invoiceForm.costPerAnimal === '' || Number(invoiceForm.costPerAnimal) < 0} onClick={saveInvoice}>
              {invoiceSaving ? 'Saving…' : 'Save cost & invoice'}
            </button>
            <button type="button" className="btn" disabled={invoiceSaving} onClick={() => setInvoiceOpen(false)}>Cancel</button>
          </div>
        </div>
      )}
      {(!vaccinations || vaccinations.length === 0) ? (
        <p className="text-sm text-avcs-muted">No vaccination or medical records for this deal.</p>
      ) : (
        <div className="admin-table-scroll tab-fill-scroll overflow-x-auto">
          <table className="min-w-full border-collapse">
            <thead>
              <tr>
                {['Medicine / type', 'Date', 'Animals', 'Batch', 'Withholding ends', 'Cost', 'Source file'].map((h, i) => (
                  <th
                    key={h}
                    className={`border-b border-[#1e6bb824] px-3 py-2 text-xs font-semibold uppercase tracking-wide text-avcs-muted ${i >= 2 && i !== 3 && i !== 4 ? 'text-right' : 'text-left'}`}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {vaccinations.map((v: any, i: number) => {
                const medicine = medicineName(v);
                const sourceEvent = v?.deal_event ? eventById[String(v.deal_event)] : null;
                const sourceHref = sourceEvent ? normalizeDocumentUrl(sourceEvent.source_file) : '';
                return (
                  <tr key={v?.id || i}>
                    <td className="border-b border-[#1e6bb814] px-3 py-2 text-sm text-avcs-text">
                      <button
                        type="button"
                        className="text-left font-semibold text-[#1e6bb8] hover:underline"
                        onClick={() => openAnimals(v)}
                        title="Show the animals covered by this record"
                      >
                        {medicine}
                      </button>
                      {v?.vaccination_type_display && v?.medicine?.name && (
                        <div className="text-xs text-avcs-muted">{v.vaccination_type_display}</div>
                      )}
                    </td>
                    <td className="border-b border-[#1e6bb814] px-3 py-2 text-sm text-avcs-muted">{fmtDate(v?.date_given)}</td>
                    <td className="border-b border-[#1e6bb814] px-3 py-2 text-right text-sm text-avcs-text">
                      {v?.animals_vaccinated != null ? fmtNum(v.animals_vaccinated) : '—'}
                    </td>
                    <td className="border-b border-[#1e6bb814] px-3 py-2 text-sm text-avcs-muted">{v?.batch_number || '—'}</td>
                    <td className="border-b border-[#1e6bb814] px-3 py-2 text-sm text-avcs-muted">{fmtDate(v?.withholding_end_date)}</td>
                    <td className="border-b border-[#1e6bb814] px-3 py-2 text-right text-sm tabular-nums text-avcs-text">{fmtMoney(v?.total_cost)}</td>
                    <td className="border-b border-[#1e6bb814] px-3 py-2 text-sm">
                      {sourceEvent ? (
                        <div className="flex flex-col gap-0.5">
                          {sourceHref ? (
                            <a
                              className="max-w-64 truncate font-mono text-xs text-[#1e6bb8] hover:underline"
                              href={sourceHref}
                              target="_blank"
                              rel="noreferrer"
                              title={sourceEvent.source_filename || 'Download source file'}
                            >
                              {sourceEvent.source_filename || 'Source file'}
                            </a>
                          ) : (
                            <span className="max-w-64 truncate text-xs text-avcs-muted">
                              {sourceEvent.source_filename || 'No source file'}
                            </span>
                          )}
                          {onViewRawData && sourceEvent.data_session && (
                            <button
                              type="button"
                              className="self-start text-xs font-semibold text-[#1e6bb8] hover:underline"
                              onClick={() => onViewRawData(sourceEvent)}
                            >
                              View raw data
                            </button>
                          )}
                        </div>
                      ) : (
                        <span className="text-xs text-avcs-muted">—</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {openVax && (
        <div
          className="fixed inset-0 z-[120] flex items-center justify-center bg-[#0b223a66] px-4 py-8"
          onClick={() => setOpenVax(null)}
        >
          <div
            className="flex max-h-full w-full max-w-2xl flex-col overflow-hidden rounded-2xl border border-[#1e6bb824] bg-white shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-3 border-b border-[#1e6bb824] px-4 py-3">
              <div className="min-w-0">
                <h3 className="text-lg font-semibold text-avcs-text">{medicineName(openVax)}</h3>
                <p className="truncate text-sm text-avcs-muted">
                  {fmtDate(openVax?.date_given)}
                  {openVax?.batch_number ? ` · batch ${openVax.batch_number}` : ''}
                  {openVax?.animals_vaccinated != null ? ` · ${fmtNum(openVax.animals_vaccinated)} recorded` : ''}
                </p>
              </div>
              <button
                type="button"
                className="text-2xl leading-none text-avcs-muted hover:text-[#6b1010]"
                onClick={() => setOpenVax(null)}
                aria-label="Close"
              >×</button>
            </div>
            <div className="overflow-auto p-4">
              {vaxLoading ? (
                <p className="text-sm text-avcs-muted">Loading animals…</p>
              ) : vaxError ? (
                <p role="alert" className="rounded-lg border border-[#e7bbb9] bg-[#f8eae7] px-3 py-2 text-sm text-[#6b1010]">{vaxError}</p>
              ) : vaxAnimals.length === 0 ? (
                <p className="text-sm text-avcs-muted">No animals are linked to this record.</p>
              ) : (
                <>
                  {/* The recorded head count and the linked animals can differ —
                      show both rather than quietly presenting one as the other. */}
                  <p className="mb-2 text-xs text-avcs-muted">
                    {fmtNum(vaxAnimals.length)} animal{vaxAnimals.length === 1 ? '' : 's'} linked to this record
                    {openVax?.animals_vaccinated != null && Number(openVax.animals_vaccinated) !== vaxAnimals.length
                      ? ` (${fmtNum(openVax.animals_vaccinated)} recorded on the entry)`
                      : ''}.
                  </p>
                  <div className="max-h-[55vh] overflow-auto rounded-lg border border-[#1e6bb824]">
                    <table className="min-w-full text-xs">
                      <thead className="sticky top-0 bg-[#f8fbff]">
                        <tr>
                          {['EID', 'VID', 'Species', 'Status', 'Location'].map((h) => (
                            <th key={h} className="whitespace-nowrap border-b border-[#1e6bb824] px-2 py-1.5 text-left font-semibold uppercase tracking-wide text-avcs-muted">{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-[#1e6bb812]">
                        {vaxAnimals.map((a) => (
                          <tr key={a.id} className="bg-white">
                            <td className="whitespace-nowrap px-2 py-1.5 font-mono text-avcs-text">{a.eid || '—'}</td>
                            <td className="whitespace-nowrap px-2 py-1.5 font-mono text-avcs-text">{a.vid || '—'}</td>
                            <td className="whitespace-nowrap px-2 py-1.5 capitalize text-avcs-text">{a.species ? String(a.species).toLowerCase() : '—'}</td>
                            <td className="whitespace-nowrap px-2 py-1.5 text-avcs-text">{a.status || '—'}</td>
                            <td className="whitespace-nowrap px-2 py-1.5 text-avcs-muted">{a.current_entity || '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </Section>
  );
}

// =============================================================================
// Integrations
// =============================================================================

const INTEGRATION_LOGOS: Record<string, string> = {
  precision_farm: '/Precision%20Farm.png',
  virtual_routez: '/VRZ.png',
  rmis: '/RMIS.png',
};

// SA Studbook & Lapersonne are not wired backend integrations yet — shown as
// explicit "planned" placeholders so the section is honest about what is live.
const PLACEHOLDER_PARTNERS: { key: string; name: string; logo?: string }[] = [
  { key: 'sa_studbook', name: 'SA Studbook', logo: '/SA%20Studbook.png' },
  { key: 'lapersonne', name: 'Lapersonne' },
];

function IntegrationStatus({ system }: { system: DealIntegrations['systems'][number] }) {
  if (!system.configured) return <span className="rounded-full bg-[#eef2f5] px-2 py-0.5 text-[11px] font-semibold text-avcs-muted">Not configured</span>;
  if (system.is_live || system.is_active) {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-semibold text-emerald-700">
        <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" aria-hidden />
        Live{system.current_environment ? ` · ${system.current_environment}` : ''}
      </span>
    );
  }
  return <span className="rounded-full bg-[#fff7ef] px-2 py-0.5 text-[11px] font-semibold text-[#7b4a18]">Inactive</span>;
}

function DealActivityChips({ activity }: { activity: Record<string, number> | null }) {
  if (!activity) return <div className="mt-2 text-[11px] text-avcs-muted">User-triggered · not deal-scoped</div>;
  const entries = Object.entries(activity).filter(([k, v]) => k !== 'total' && (v || 0) > 0);
  if ((activity.total || 0) === 0) return <div className="mt-2 text-[11px] text-avcs-muted">No activity for this deal yet</div>;
  return (
    <div className="mt-2 flex flex-wrap gap-1.5">
      {entries.map(([status, count]) => (
        <span key={status} className="inline-flex items-center gap-1">
          <StatusPill status={status} />
          <span className="text-[11px] font-semibold text-avcs-text">{fmtNum(count)}</span>
        </span>
      ))}
    </div>
  );
}

function PartnerLogo({ name, logo }: { name: string; logo?: string }) {
  const [ok, setOk] = useState(Boolean(logo));
  if (logo && ok) {
    return (
      <div className="flex h-9 w-full items-center justify-start">
        <img src={logo} alt={name} className="max-h-full max-w-[120px] object-contain" onError={() => setOk(false)} />
      </div>
    );
  }
  return <div className="text-base font-bold text-avcs-text">{name}</div>;
}

function IntegrationsSection({ integrations }: { integrations: DealIntegrations | null }) {
  const systems = integrations?.systems || [];
  return (
    <Section title="Integrations" subtitle="Third-party systems and this deal's activity with each.">
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
        {systems.map((system) => (
          <div key={system.key} className="rounded-xl border border-[#1e6bb824] bg-white p-3">
            <div className="flex items-start justify-between gap-2">
              <PartnerLogo name={system.name} logo={INTEGRATION_LOGOS[system.key]} />
              <IntegrationStatus system={system} />
            </div>
            <div className="mt-1 text-[11px] font-semibold uppercase tracking-wide text-avcs-muted">{system.name}</div>
            <DealActivityChips activity={system.deal_activity} />
          </div>
        ))}

        {PLACEHOLDER_PARTNERS.map((partner) => (
          <div key={partner.key} className="rounded-xl border border-dashed border-[#1e6bb833] bg-[#fbfeff] p-3 opacity-80">
            <div className="flex items-start justify-between gap-2">
              <PartnerLogo name={partner.name} logo={partner.logo} />
              <span className="rounded-full bg-[#eef2f5] px-2 py-0.5 text-[11px] font-semibold text-avcs-muted">Not connected</span>
            </div>
            <div className="mt-1 text-[11px] font-semibold uppercase tracking-wide text-avcs-muted">{partner.name}</div>
            <div className="mt-2 text-[11px] text-avcs-muted">Planned integration</div>
          </div>
        ))}
      </div>
      {systems.length === 0 && (
        <p className="mt-2 text-xs text-avcs-muted">Live integration status is available to admin users.</p>
      )}
    </Section>
  );
}

/**
 * Per-animal feed for the deal. Closed deal: the split stored at close, final.
 * Open deal: a provisional split as of today, computed on request and not
 * stored — animals still at the feedlot keep accruing days until the close.
 */
function FeedAllocationPanel({ dealId, isCompleted, refreshKey }: { dealId: string; isCompleted: boolean; refreshKey: string }) {
  const [data, setData] = useState<DealFeedAllocation | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showAll, setShowAll] = useState(false);
  const load = useCallback(async () => {
    if (!dealId) return;
    setLoading(true);
    setError('');
    try {
      setData(await fetchDealFeedAllocation(dealId));
    } catch (e: any) {
      setError(e?.message || 'Could not work out the per-animal feed.');
    } finally {
      setLoading(false);
    }
  }, [dealId]);
  // A closed deal's split is stored: show it unasked. An open deal's is
  // provisional: compute only when the operator asks.
  useEffect(() => { setData(null); if (isCompleted) load(); }, [isCompleted, load, refreshKey]);

  const rows = data ? (showAll ? data.animals : data.animals.slice(0, 25)) : [];
  return (
    <div className="mt-4 rounded-xl border border-[#1e6bb824] bg-[#fbfeff] p-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <div className="text-sm font-semibold text-avcs-text">Feed per animal</div>
          <div className="text-xs text-avcs-muted">
            {isCompleted
              ? "Final: split when the deal closed, by each animal's days at the entity over its whole stay."
              : 'Provisional until the deal closes — animals still at the feedlot are counted to today and the shares move daily.'}
          </div>
        </div>
        {!isCompleted && (
          <button type="button" className="btn" disabled={loading || !dealId} onClick={load}>{loading ? 'Working…' : data ? 'Recompute as of today' : 'Provisional split as of today'}</button>
        )}
      </div>
      {error && <div className="mt-2 rounded-lg border border-[#e7bbb9] bg-[#f8eae7] px-3 py-2 text-sm text-[#6b1010]">{error}</div>}
      {loading && !data && <p className="mt-2 text-sm text-avcs-muted">Working out each animal's share…</p>}
      {data && (
        <>
          <div className="mt-3 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
            {[
              ['Feed allocated', `${fmtNum(data.totals.kg)} kg`],
              ['Feed cost allocated', fmtMoney(data.totals.cost)],
              ['Weight gain (covered animals)', `${fmtNum(data.totals.gainKg)} kg`],
              ['Deal FCR', data.totals.fcr != null ? Number(data.totals.fcr).toFixed(2) : '—'],
            ].map(([label, value]) => (
              <div key={label} className="rounded-lg border border-[#1e6bb829] bg-white p-3">
                <div className="text-xs font-semibold uppercase tracking-wide text-avcs-muted">{label}</div>
                <div className="mt-1 text-xl font-bold text-avcs-text">{value}</div>
              </div>
            ))}
          </div>
          <div className="mt-2 text-xs text-avcs-muted">
            {data.stored ? 'Stored at close.' : `Provisional as of ${data.asOf}.`} {data.totals.animals} of {data.animals.length} animals covered
            {data.sources.length > 0 ? ` · ${data.sources.length} feed source${data.sources.length === 1 ? '' : 's'}: ${data.sources.map((s) => `${s.label} (${s.entity || '—'}, ${fmtNum(s.animalDays)} animal-days)`).join('; ')}` : ' · no feed sources with an entity yet'}
          </div>
          {data.unallocated.length > 0 && (
            <div className="mt-2 rounded-lg border border-[#ecd1b9] bg-[#fff7ef] px-3 py-2 text-xs text-[#7b4a18]">
              {data.unallocated.length} animal{data.unallocated.length === 1 ? '' : 's'} never had a receive event at a feed entity, so they carry no feed: {data.unallocated.slice(0, 8).map((a) => a.eid || a.vid || a.animal.slice(0, 8)).join(', ')}{data.unallocated.length > 8 ? '…' : ''}. Fix the movement chain before closing.
            </div>
          )}
          {data.animals.length > 0 && (
            <div className="admin-table-scroll mt-3 overflow-x-auto">
              <table className="min-w-full border-collapse text-sm">
                <thead>
                  <tr className="border-b border-[#1e6bb824] text-left text-xs uppercase tracking-wide text-avcs-muted">
                    <th className="px-2 py-1.5 font-semibold">Animal</th>
                    <th className="px-2 py-1.5 text-right font-semibold">Days</th>
                    <th className="px-2 py-1.5 text-right font-semibold">Feed kg</th>
                    <th className="px-2 py-1.5 text-right font-semibold">Feed cost</th>
                    <th className="px-2 py-1.5 text-right font-semibold">First kg</th>
                    <th className="px-2 py-1.5 text-right font-semibold">Last kg</th>
                    <th className="px-2 py-1.5 text-right font-semibold">Gain kg</th>
                    <th className="px-2 py-1.5 text-right font-semibold">FCR</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.animal} className={`border-b border-[#1e6bb814] ${r.covered ? '' : 'text-avcs-muted'}`}>
                      <td className="px-2 py-1.5 font-mono text-xs">{r.eid || r.vid || r.animal.slice(0, 8)}</td>
                      <td className="px-2 py-1.5 text-right tabular-nums">{r.days || '—'}</td>
                      <td className="px-2 py-1.5 text-right tabular-nums">{r.kg != null ? fmtNum(r.kg) : '—'}</td>
                      <td className="px-2 py-1.5 text-right tabular-nums">{r.cost != null ? fmtMoney(r.cost) : '—'}</td>
                      <td className="px-2 py-1.5 text-right tabular-nums">{r.firstWeight != null ? fmtNum(r.firstWeight) : '—'}</td>
                      <td className="px-2 py-1.5 text-right tabular-nums">{r.lastWeight != null ? fmtNum(r.lastWeight) : '—'}</td>
                      <td className="px-2 py-1.5 text-right tabular-nums">{r.gainKg != null ? fmtNum(r.gainKg) : '—'}</td>
                      <td className="px-2 py-1.5 text-right font-semibold tabular-nums">{r.fcr != null ? Number(r.fcr).toFixed(2) : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {data.animals.length > 25 && (
                <button type="button" className="mt-2 text-xs font-semibold text-[#1e6bb8] hover:underline" onClick={() => setShowAll((v) => !v)}>
                  {showAll ? 'Show first 25' : `Show all ${data.animals.length} animals`}
                </button>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

/**
 * Wool for this deal from the shared records that reached it: kilograms and
 * income divided by the sheep shorn (the animals on the shearing events),
 * shearing and pack costs alongside.
 */
function WoolSummary({ dealId, refreshKey }: { dealId: string; refreshKey: number }) {
  const [rows, setRows] = useState<Awaited<ReturnType<typeof fetchSharedFinances>>>([]);
  useEffect(() => {
    if (!dealId) return;
    let cancelled = false;
    fetchSharedFinances({ dealId }).then((r) => { if (!cancelled) setRows(r); }).catch(() => { if (!cancelled) setRows([]); });
    return () => { cancelled = true; };
  }, [dealId, refreshKey]);
  const mine = (type: string) => rows.filter((r) => r.recordType === type).map((r) => r.allocations.find((a) => a.dealId === dealId)).filter(Boolean) as { amount: number; quantityKg: number | null; animalCount: number }[];
  const sales = mine('WOOL_SALE');
  if (sales.length === 0 && mine('SHEARING').length === 0) return null;
  const income = sales.reduce((s, a) => s + a.amount, 0);
  const kg = sales.reduce((s, a) => s + (a.quantityKg || 0), 0);
  const sheep = sales.length > 0 ? Math.max(...sales.map((a) => a.animalCount)) : Math.max(0, ...mine('SHEARING').map((a) => a.animalCount));
  const shearing = mine('SHEARING').reduce((s, a) => s + a.amount, 0);
  return (
    <div className="mt-3 rounded-xl border border-[#1e6bb824] bg-white p-3">
      <div className="mb-2 text-sm font-semibold text-avcs-text">Wool</div>
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-5">
        {[
          ['Sheep shorn', fmtNum(sheep)],
          ['Wool kg', kg > 0 ? `${fmtNum(kg)} kg` : '—'],
          ['Wool kg / sheep', kg > 0 && sheep > 0 ? `${(kg / sheep).toFixed(2)} kg` : '—'],
          ['Wool income', income > 0 ? fmtMoney(income) : '—'],
          ['Income / sheep', income > 0 && sheep > 0 ? fmtMoney(income / sheep) : '—'],
        ].map(([label, value]) => (
          <div key={label} className="rounded-lg border border-[#1e6bb829] bg-[#fbfeff] p-3">
            <div className="text-xs font-semibold uppercase tracking-wide text-avcs-muted">{label}</div>
            <div className="mt-1 text-lg font-bold text-avcs-text">{value}</div>
          </div>
        ))}
      </div>
      {shearing > 0 && <div className="mt-2 text-xs text-avcs-muted">Shearing cost on this deal: {fmtMoney(shearing)}{sheep > 0 ? ` · ${fmtMoney(shearing / sheep)} per sheep` : ''}.</div>}
    </div>
  );
}
